import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import type { BundleTemplate } from "@/types/bundleTemplate";
import type { DeckTemplate } from "@/types/deckTemplate";
import { useAuth, roleAtLeast } from "@/lib/auth";
import {
  fetchCardMeta,
  isUpstreamRateLimited,
  mapRarity,
  searchCardByNameAndNumber,
} from "@/features/tcgApi";
import { splitMtgTypeLine } from "@tcg-web/normalize";
import {
  DEFAULT_BUNDLE_SETTINGS,
  type BundleSettings,
  type BundleStrategyId,
} from "@/features/bundleEngine";
import { backfillCardSets } from "@/features/cardSetBackfill";
import {
  fetchCards as apiFetchCards,
  fetchLots as apiFetchLots,
  fetchLocations as apiFetchLocations,
  fetchContainers as apiFetchContainers,
  syncBulkDeleteCards,
  syncBulkPatchCards,
  syncCreateCard,
  syncCreateCards,
  syncCreateLocation,
  syncCreateLot,
  syncDeleteCard,
  syncDeleteLot,
  syncUpdateCard,
  syncUpdateLocation,
  syncUpdateLot,
  archiveLocationRequest,
  restoreLocationRequest,
  syncCreateContainer,
  syncUpdateContainer,
  archiveContainerRequest,
  restoreContainerRequest,
  deleteContainerRequest,
  archiveCardRequest,
  restoreCardRequest,
  uploadAllInventory,
} from "./inventorySync";
import {
  fetchProducts,
  fetchSales,
  fetchLiveListings,
  syncCreateProduct,
  syncUpdateProduct,
  syncDeleteProduct,
  syncCreateSale,
  syncUpdateSale,
  syncDeleteSale,
  cancelSaleRequest,
  type CancelSaleBody,
  refundSaleRequest,
  type RefundSaleBody,
  syncCreateLiveListing,
  syncUpdateLiveListing,
  syncDeleteLiveListing,
  createSale,
  endLiveListingRequest,
  archiveProductRequest,
  restoreProductRequest,
  fetchListingTemplates,
  fetchDeckTemplates,
  fetchBundleTemplates,
  fetchStoreSettings,
  fetchShippingSupplies,
  fetchPriceEntries,
  syncCreateListingTemplate,
  syncUpdateListingTemplate,
  syncDeleteListingTemplate,
  syncCreateDeckTemplate,
  syncDeleteDeckTemplate,
  syncCreateBundleTemplate,
  syncUpdateBundleTemplate,
  syncDeleteBundleTemplate,
  syncCreateShippingSupply,
  syncUpdateShippingSupply,
  syncDeleteShippingSupply,
  restockShippingSupply,
  syncCreatePriceEntry,
  syncDeletePriceEntry,
  syncStoreSettings,
  saveStoreSettings,
  type StoreSettings,
} from "./catalogSync";

// ─── Types (ported from mobile AppContext, subset) ───────────────────────────

export type LotStatus =
  | "Unsorted"
  | "Sorting"
  | "Sorted"
  | "Listed"
  | "Partially Sold"
  | "Closed";

export type LotType =
  | "Collection"
  | "Booster Pack"
  | "Booster Box"
  | "Elite Trainer Box"
  | "Special Pack"
  | "Bulk";

// Canonical rarity + finish types now live in @tcg-web/normalize so the web
// and API server share a single source of truth. Re-exported here to keep the
// large set of `import { type CardRarity } from "@/context/AppContext"` call
// sites working unchanged.
import type { CardFinish, CardRarity } from "@tcg-web/normalize";
export type { CardFinish, CardRarity };

export type CardCondition = "NM" | "LP" | "MP" | "HP" | "Damaged";
export type CardGame = "Pokemon" | "Magic" | "Yu-Gi-Oh" | "One Piece" | "Other";

export type SortingCategory =
  | "Common/Uncommon Bulk"
  | "Trainers/Items/Supporters"
  | "Energy"
  | "Rares"
  | "Reverse Holos"
  | "Holos"
  | "Hits"
  | "Promos"
  | "Damaged"
  | "Sealed Product"
  | "Needs Review";

export type SaleVenue =
  | "eBay"
  | "Facebook Marketplace"
  | "Local"
  | "TCGplayer"
  | "Website"
  | "Other";

export type PriceSource = "eBay Sold" | "TCGplayer" | "Manual" | "Other";

export type ProductType =
  | "100-card Random Lot"
  | "50-card Themed Lot"
  | "25-card Trainer Lot"
  | "Holo/Reverse Bundle"
  | "4x Playset"
  | "Mystery Pack"
  | "Starter/Kid Pack"
  | "Premium Single"
  | "Sealed Bundle"
  | "Pre-built Deck"
  | "Deck Core"
  | "Deck Upgrade Kit"
  | "Set Builder Lot"
  | "Character Theme Bundle"
  | "Type Theme Bundle"
  | "Trainer Staples Kit"
  | "Energy Kit"
  | "Custom"
  /** TNK-112 listing import. Shell product whose cards live on eBay
   *  (operator hasn't linked local inventory). UI surfaces a banner +
   *  list-view tag so it's never mistaken for a fully-tracked bundle. */
  | "Imported";

export type ProductStatus =
  | "Draft"
  | "Ready to List"
  | "Listed"
  | "Sold"
  | "Picked"
  | "Packed"
  | "Shipped";

export interface SortingBin {
  /** Sorting-bin id, game-defined (see features/tcgGameConfig.ts). Pokémon
   *  values match the legacy `SortingCategory` union; other games define
   *  their own. Typed `string` so any game's taxonomy round-trips. */
  category: string;
  count: number;
  estimatedCount?: number;
  location: string;
  notes: string;
  costBasis: number;
}

export interface PurchaseLot {
  id: string;
  purchaseDate: string;
  seller: string;
  purchaseLocation?: string;
  /** TCG this lot belongs to. Undefined = Pokémon (legacy default). Drives
   *  lot-type + sorting-bin taxonomy via configForGame(). */
  game?: CardGame;
  /** Game-defined lot product type (Pokémon: "Booster Box"/"Elite Trainer
   *  Box"; Magic: "Commander Deck"/"Bundle"; …). Typed `string` so each
   *  game's set round-trips. */
  lotType?: string;
  purchaseCost: number;
  shippingCost: number;
  taxFees: number;
  estimatedCards: number;
  notes: string;
  status: LotStatus;
  sortingBins: SortingBin[];
  createdAt: string;
  ebayOrderId?: string;
  receiptNumber?: string;
}

export interface PriceHistoryEntry {
  price: number;
  source: PriceSource;
  recordedAt: string;
}

export interface Card {
  id: string;
  name: string;
  game: CardGame;
  setName: string;
  setCode: string;
  cardNumber: string;
  rarity: CardRarity;
  variant: string;
  condition: CardCondition;
  quantity: number;
  sourceLotId: string;
  costBasis: number;
  marketPrice: number;
  expectedSalePrice: number;
  storageLocation: string;
  listingStatus: "Unlisted" | "Listed" | "Sold" | "In Package";
  packagedProductId?: string | null;
  saleVenue: SaleVenue | "";
  // TNK-212 — per-card storefront override: hide this single from the public
  // store even when it passes the eligibility gate.
  storefrontHidden?: boolean;
  ebayListingId?: string;
  ebayListingUrl?: string;
  notes: string;
  createdAt: string;
  tcgSupertype?: string;
  tcgSubtypes?: string[];
  tcgTypes?: string[];
  tcgHp?: string;
  tcgArtist?: string;
  tcgPokedexNumbers?: number[];
  tcgSeries?: string;
  // MTG-native printing data (TNK-121). Pokemon + other games leave undefined.
  /** Scryfall's mana_cost string, e.g. "{2}{U}{U}", "{X}{R}", or "" for
   *  Lands. Includes hybrid `{U/R}` and Phyrexian `{U/P}` tokens. */
  mtgManaCost?: string;
  /** Joined power/toughness for creatures, e.g. `3/4`, `* / *`, `X/X`.
   *  Stored as Scryfall returns — character literals like `*` and `X`
   *  pass through verbatim. */
  mtgPowerToughness?: string;
  /** Loyalty for planeswalkers, e.g. "4". */
  mtgLoyalty?: string;
  /** Full oracle (rules) text with reminder text in italics. */
  mtgOracleText?: string;
  /** Color identity from Scryfall — array of `W/U/B/R/G`. Empty = colorless. */
  mtgColors?: string[];
  /** Year the card was released. Free-form string ("2024") so non-numeric
   *  edge cases (promo years like "2024-2025") still round-trip. */
  year?: string;
  /** Print treatment. `undefined` = regular non-foil. eBay's *C:Finish is
   *  the source of truth on CSV imports; mapFinish() derives it from the
   *  pokemontcg.io rarity string for scan/search paths. */
  finish?: CardFinish;
  /** ISO language code or display name ("English", "Japanese"). Undefined
   *  means English by default — only set when explicitly non-English. */
  language?: string;
  /** True when the card has been professionally graded. eBay's *C:Graded
   *  populates this on import. */
  graded?: boolean;
  /** Grading company when graded (e.g. "PSA", "BGS", "CGC"). Undefined when
   *  not graded. `graded` stays in sync (true when this is set). */
  gradingCompany?: string;
  /** /api/images id (e.g. "abc-def.jpg") for the card's front photo. The
   *  full URL is `/api/images/{imageFrontRef}`. Undefined when no photo. */
  imageFrontRef?: string;
  /** /api/images id for the card's back photo. */
  imageBackRef?: string;
  /** Original source URL the front photo was fetched from (eBay PicURL,
   *  Cardmarket photo URL when added in the future, etc.). Persisted so a
   *  retry-missing-images action can re-attempt failed fetches without
   *  needing the original CSV. Undefined for user-uploaded photos. */
  imageFrontSourceUrl?: string;
  /** Same as imageFrontSourceUrl for the back photo. */
  imageBackSourceUrl?: string;
  priceSource?: PriceSource;
  pricedAt?: string;
  priceHistory?: PriceHistoryEntry[];
  /** Soft-delete timestamp (TNK-109). Set by archiveCard, cleared by
   *  restoreCard. fetchCards pulls archived rows alongside active ones so the
   *  inventory tabs can switch between them without re-fetching; default
   *  views filter `archivedAt == null`. */
  archivedAt?: string;
}

export interface Product {
  id: string;
  productType: ProductType;
  name: string;
  quantity: number;
  cardCount: number;
  cardIds: string[];
  sourceLotId: string;
  costBasis: number;
  packagingCost: number;
  targetSalePrice: number;
  listingTitle: string;
  description: string;
  storageLocation: string;
  status: ProductStatus;
  /** TNK-212 phase 2 — operator opt-in to surface this bundle in the website
   *  storefront. Distinct from a marketplace `status="Listed"`; cards are held
   *  from when the bundle is made, so it's listable until Sold. Server-guarded. */
  storefrontListed?: boolean;
  /** TNK-212 phase 2 — optional custom storefront cover photo (image ref).
   *  Null/absent = the store feed falls back to a member card's photo. */
  coverImageRef?: string | null;
  createdAt: string;
  supplyIds?: string[];
  bundleStrategyId?: BundleStrategyId;
  deckBuild?: unknown;
  externalDeckSource?: unknown;
  /** Soft-delete timestamp (TNK-102). Set by archiveProduct, cleared by
   *  restoreProduct. Default product views filter `archivedAt == null`;
   *  the Archived tab shows the rest. Cards stay reserved in their
   *  packagedProductId state while archived. */
  archivedAt?: string;
}

export interface StorageBin {
  id: string;
  name: string;
  color: string;
  /** Soft-pointer to a Location row (TNK-103 Phase G). Optional for legacy
   *  rows that haven't been migrated; the seed step backfills every bin to
   *  the store's auto-created "Home" location, and the UI defaults new
   *  bins there too. */
  locationId?: string;
  createdAt: string;
}

/** Physical site that contains StorageBins. Seeded as "Home" per store; the
 *  Location selector only surfaces in the UI when more than one exists, so
 *  single-location stores never see it. */
export interface Location {
  id: string;
  name: string;
  kind: "storage" | "retail";
  archivedAt?: string;
  createdAt: string;
}

/** A node in the arbitrary-depth storage tree (TNK-187 / epic TNK-186) —
 *  site / case / shelf / binder / bin / box / unsorted. `parentId` is a
 *  soft-pointer (NULL = top-level). Additive in Phase A; runs alongside
 *  StorageBin + Location, which are mirrored into containers. */
export interface Container {
  id: string;
  parentId?: string;
  kind: string;
  name: string;
  color?: string;
  label?: string;
  capacityMax?: number;
  state: string;
  locationNotes?: string;
  archivedAt?: string;
  closedAt?: string;
  createdAt: string;
}

export interface SaleSupplyItem {
  id: string;
  name: string;
  unitCost: number;
  /** Snapshot of how many units this sale consumed (TNK-101). Filled by the
   *  server on POST /sales using the supply's unitsPerUse + consumption +
   *  the request's cardCount. Older sale rows may not have it set. */
  consumedQty?: number;
}

export interface Sale {
  id: string;
  saleDate: string;
  itemType: "Card" | "Product";
  itemId: string;
  itemName: string;
  venue: SaleVenue;
  salePrice: number;
  buyerShipping: number;
  actualShipping: number;
  platformFees: number;
  paymentFees: number;
  packagingCost: number;
  sourceLotId: string;
  /** Where the sold card lived, snapshotted at sale-time (TNK-193). Drives the
   *  sales-by-location report. Null for product/bundle + unattributed sales. */
  containerId?: string;
  containerPath?: string;
  trackingNumber: string;
  /** eBay-valid carrier code captured on Mark Shipped (USPS / FEDEX / UPS /
   *  DHL / OTHER); needed in addition to trackingNumber when pushing the
   *  fulfillment to eBay. */
  carrierCode?: string;
  /** Lifecycle (TNK-101). `shipped` stays as the terminal flag; `packed` is
   *  the intermediate state. */
  packed?: boolean;
  packedAt?: string;
  shipped: boolean;
  shippedAt?: string;
  buyerName?: string;
  buyerAddress?: string;
  ebayOrderId?: string;
  ebayTransactionId?: string;
  ebayFulfillmentId?: string;
  /** Winning listing (TNK-102). Soft-pointer to the LiveListing.id of the
   *  listing whose close created this sale. Optional for manually logged
   *  sales that never had a listing. */
  listingId?: string;
  /** Pick-step completion timestamp (TNK-102). Mark Packed is gated on this
   *  in the UI; null until every pick_list row has been ticked off. */
  pickedAt?: string;
  /** Operator-initiated cancellation (TNK-111). Set by cancelSale; the
   *  derived "Cancelled" status renders red and supersedes Sold/Picked/
   *  Packed in the badge. */
  cancelledAt?: string;
  cancelReason?: string;
  /** eBay-confirmed refund timestamp + amount in cents (TNK-111). Populated
   *  by the after-commit eBay call on successful cancellation; refundCents
   *  stays null until eBay's refund webhook lands (future work). */
  refundedAt?: string;
  refundCents?: number;
  /** Outcome of the async eBay cancellation call (TNK-159 / W4). One of
   *  `"ok"`, `"failed: <msg>"`, `"skipped"`, `"not-connected"`. Null while
   *  the async call is still in flight. */
  ebayCancelStatus?: string;
  /** Outcome of the async eBay issue-refund call (TNK-175). Same vocabulary as
   *  ebayCancelStatus. */
  ebayRefundStatus?: string;
  /** Operator's reason for the refund (TNK-175). */
  refundReason?: string;
  createdAt: string;
  suppliesBreakdown?: SaleSupplyItem[];
  /** ISO timestamp the live listing was first posted, captured at completeSale time so sell-through analytics survive listing status changes. */
  listedAt?: string;
}

/** Lifecycle of a marketplace listing (TNK-102). Listings persist after sale
 *  — `status` flows Active → (Ended | Sold | Cancelled). Multiple Active
 *  listings per product are allowed (cross-listing); when one closes, the
 *  others auto-flip to Cancelled with endReason='superseded'. */
export type ListingStatus = "Active" | "Ended" | "Sold" | "Cancelled";

export interface LiveListing {
  id: string;
  productId: string;
  title: string;
  venue: SaleVenue;
  askingPrice: number;
  cardCount: number;
  startedAt: string;
  endsAt?: string;
  ebayListingId?: string;
  ebayListingUrl?: string;
  listingType?: "FixedPrice" | "Auction";
  supplyIds?: string[];
  /** Auction-only: original starting bid (StartPrice). Captured at go-live
   *  and updated when the seller revises price. Optional for backwards-compat. */
  startingBid?: number;
  /** Auction-only: reserve price (when one is set). */
  reservePrice?: number;
  /** Auction-only: optional Buy It Now price. */
  buyItNowPrice?: number;
  /** Lifecycle (TNK-102). Defaults to "Active" if absent on legacy in-memory
   *  rows; server always supplies it. */
  status?: ListingStatus;
  endedAt?: string;
  endReason?: string;
  externalEndedOk?: boolean;
}

/** Summary of a listing auto-ended because a sibling sale closed
 *  (cross-listing first-sale-wins). Returned by completeSale so the
 *  caller can render a toast like "Ended 2 other listings (eBay: ok,
 *  Facebook: failed)". */
export interface SupersededListingSummary {
  id: string;
  venue: SaleVenue;
  ebayListingId?: string;
}

export interface ListingTemplate {
  id: string;
  name: string;
  body: string;
  createdAt: string;
}

export interface RestockEvent {
  id: string;
  supplyId: string | null;
  supplyName: string;
  addedQty: number;
  totalCost: number;
  perUnitCost: number;
  supplier?: string;
  recordedAt: string;
}

export type SupplyConsumption = "per-listing" | "per-card";

export interface ShippingSupply {
  id: string;
  name: string;
  unitCost: number;
  quantity?: number;
  createdAt: string;
  enabled?: boolean;
  unitsPerUse?: number;
  /** Whether `unitsPerUse` is consumed once per listing (most items) or scales
   *  with the listing's card count (e.g. penny sleeves: 1 per card). */
  consumption?: SupplyConsumption;
  restockHistory?: RestockEvent[];
}

/** Cost contribution of a supply when it's added to a single listing. For
 *  per-listing supplies it's just unitCost × unitsPerUse; for per-card supplies
 *  it scales with the listing's cardCount. Used by Go-Live, live listings, and
 *  sale snapshots so they all stay in sync. */
export function supplyCostFor(
  s: {
    unitCost: number;
    unitsPerUse?: number;
    consumption?: SupplyConsumption;
  },
  cardCount = 1,
): number {
  const base = s.unitCost * (s.unitsPerUse ?? 1);
  return s.consumption === "per-card" ? base * Math.max(0, cardCount) : base;
}

export interface PriceEntry {
  id: string;
  cardName: string;
  setName: string;
  setCode: string;
  cardNumber: string;
  condition: CardCondition;
  price: number;
  source: PriceSource;
  notes: string;
  loggedAt: string;
}

// Re-exported for consumers that previously imported them from mobile AppContext.
export type { BundleTemplate } from "@/types/bundleTemplate";
export type { DeckTemplate } from "@/types/deckTemplate";

// ─── Calc helpers (ported verbatim) ──────────────────────────────────────────

export function generateId(prefix: string): string {
  const time = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${prefix}-${time}${rand}`;
}

export function calcLandedCost(lot: PurchaseLot): number {
  return lot.purchaseCost + lot.shippingCost + lot.taxFees;
}

export function calcCostPerCard(lot: PurchaseLot): number {
  const landed = calcLandedCost(lot);
  return lot.estimatedCards > 0 ? landed / lot.estimatedCards : 0;
}

export function calcGlobalCostPerCard(lots: PurchaseLot[]): number {
  const qualifying = lots.filter((l) => l.estimatedCards > 0);
  const totalCost = qualifying.reduce((s, l) => s + calcLandedCost(l), 0);
  const totalCards = qualifying.reduce((s, l) => s + l.estimatedCards, 0);
  return totalCards > 0 ? totalCost / totalCards : 0;
}

export function calcNetProfit(sale: Sale): number {
  return (
    sale.salePrice +
    sale.buyerShipping -
    sale.actualShipping -
    sale.platformFees -
    sale.paymentFees -
    sale.packagingCost
  );
}

export function calcProfitMargin(sale: Sale): number {
  const gross = sale.salePrice + sale.buyerShipping;
  if (gross === 0) return 0;
  return (calcNetProfit(sale) / gross) * 100;
}

// ─── Persistence ─────────────────────────────────────────────────────────────

// ─── Seed data (starter supplies + listing templates) ───────────────────────

export const STARTER_SHIPPING_SUPPLIES_CATALOG: ShippingSupply[] = [
  {
    id: "sup-starter-1",
    name: "Penny sleeve",
    unitCost: 0.02,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-2",
    name: "Toploader",
    unitCost: 0.13,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-3",
    name: "Semi-rigid holder / Card Saver",
    unitCost: 0.18,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-4",
    name: "Team bag",
    unitCost: 0.05,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-5",
    name: "Painter's tape / pull tab",
    unitCost: 0.02,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-6",
    name: "Cardboard stiffener / shipping shield",
    unitCost: 0.13,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-7",
    name: "Plain envelope / PWE",
    unitCost: 0.07,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-8",
    name: "Rigid card envelope / eBay card envelope",
    unitCost: 0.25,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-9",
    name: "Bubble mailer #000 / #0",
    unitCost: 0.4,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-10",
    name: "4x6 or 6x9 rigid mailer",
    unitCost: 0.5,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-11",
    name: "Small box",
    unitCost: 1.0,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-12",
    name: "Thermal label / ink label",
    unitCost: 0.06,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-13",
    name: "Shipping scale",
    unitCost: 17.5,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "sup-starter-14",
    name: "Label printer",
    unitCost: 120.0,
    quantity: 0,
    enabled: false,
    unitsPerUse: 1,
    createdAt: new Date(0).toISOString(),
  },
];

const STARTER_LISTING_TEMPLATES: ListingTemplate[] = [
  {
    id: "tmpl-starter-1",
    name: "Standard Single Card",
    body: "{{cardName}} - {{setName}}\n\nCondition: {{condition}}\nPrice: {{price}}\n\nCard ships in a protective sleeve and top loader inside a padded envelope. Fast dispatch — usually same or next business day.\n\nPlease check my other listings for more singles and bundles!",
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "tmpl-starter-2",
    name: "Bundle Lot (auto-generated)",
    body: "{{generated}}\n\nAll cards ship together in protective sleeves inside a rigid top loader or card saver. Bundle deals available — send an offer!\n\nCheck out my store for more lots and singles.",
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "tmpl-starter-3",
    name: "Holo / Rare Card",
    body: "{{cardName}} Holo - {{setName}}\n\nCondition: {{condition}}\nAsking: {{price}}\n\nThis card has been stored in a sleeve and top loader since pulled. Photos show actual card — what you see is what you get.\n\nShips quickly in rigid protection. International buyers welcome — contact me for a shipping quote.",
    createdAt: new Date(0).toISOString(),
  },
];

const STARTER_BUNDLE_TEMPLATES: BundleTemplate[] = [
  {
    id: "btpl-starter-water",
    name: "Water Pokémon Bundle",
    intent: "Theme",
    targetCount: 50,
    filters: { game: "Pokemon", supertypes: ["Pokemon"], types: ["Water"] },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-fire",
    name: "Fire Pokémon Bundle",
    intent: "Theme",
    targetCount: 50,
    filters: { game: "Pokemon", supertypes: ["Pokemon"], types: ["Fire"] },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-lightning",
    name: "Lightning Pokémon Bundle",
    intent: "Theme",
    targetCount: 50,
    filters: { game: "Pokemon", supertypes: ["Pokemon"], types: ["Lightning"] },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-grass",
    name: "Grass Pokémon Bundle",
    intent: "Theme",
    targetCount: 50,
    filters: { game: "Pokemon", supertypes: ["Pokemon"], types: ["Grass"] },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-psychic",
    name: "Psychic Pokémon Bundle",
    intent: "Theme",
    targetCount: 50,
    filters: { game: "Pokemon", supertypes: ["Pokemon"], types: ["Psychic"] },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-holo-pack",
    name: "Holo & Reverse Holo Pack",
    intent: "Theme",
    targetCount: 10,
    filters: { finishes: ["Holo", "Reverse Holo"] },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-bulk-filler",
    name: "Common/Uncommon Bulk Filler",
    intent: "Random",
    targetCount: 100,
    filters: { rarities: ["Common", "Uncommon"] },
    selection: { profile: "bulk" },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-pokemon-mystery",
    name: "Pokémon Mystery Mix",
    intent: "Random",
    targetCount: 100,
    filters: { game: "Pokemon" },
    selection: { profile: "bulk" },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-mystery-pack",
    name: "Mystery Pack",
    intent: "Random",
    targetCount: 20,
    filters: {},
    selection: { profile: "shuffle" },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-premium-hit-hunt",
    name: "Premium Hit Hunt",
    intent: "Random",
    targetCount: 25,
    filters: { rarities: ["Rare"], finishes: ["Holo", "Premium"] },
    selection: { profile: "premium" },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-50-themed-mystery",
    name: "50-Card Themed Mystery",
    intent: "Random",
    targetCount: 50,
    filters: { game: "Pokemon" },
    selection: { profile: "standard" },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-trainer-staples",
    name: "Trainer Staples",
    intent: "Trainer/Energy",
    targetCount: 25,
    filters: { supertypes: ["Trainer"] },
    createdAt: new Date(0).toISOString(),
  },
  {
    id: "btpl-starter-basic-energy",
    name: "Basic Energy Pack",
    intent: "Trainer/Energy",
    targetCount: 15,
    filters: { supertypes: ["Energy"] },
    createdAt: new Date(0).toISOString(),
  },
];

// ─── Persistence ─────────────────────────────────────────────────────────────

export type StoreImageKind = "logo" | "watermark";
export type StoreImages = Partial<Record<StoreImageKind, string>>;

export type SuppliesTrackingMode = "off" | "cost-only" | "full";

export interface SuppliesPreferences {
  trackingMode: SuppliesTrackingMode;
  /** True once the operator has been through the init wizard for this store. */
  initialized: boolean;
}

/** TNK-154 — operator workbench theme. Two preset families:
 *  - light family: `light` (default stark white), `ivory` (warm dim off-white)
 *  - dark family: `dark` (default), `midnight` (deep purple)
 *  Plus `system` which follows `prefers-color-scheme` and resolves to
 *  the plain `light` or `dark` packs. Storefront is unaffected — it
 *  keeps its own dark+gold scope under `.shop-theme`. */
export type Theme = "light" | "ivory" | "dark" | "midnight" | "system";

export interface AppPreferences {
  /** Market price (USD) a card must exceed to escape the bulk pile and earn a
   *  single/playset listing recommendation. TCGplayer's bulk cutoff is $0.24. */
  bulkThresholdUsd: number;
  /** Copies of one identity needed before a playset is suggested. */
  playsetMinQty: number;
  /** Supplies tracking tier — controls how the /tools/supplies page renders
   *  and how packaging cost is computed at go-live. Absent on legacy stores;
   *  the wizard sets `initialized: true` once the operator picks. */
  supplies?: SuppliesPreferences;
  /** Operator workbench theme (TNK-154). Absent = system. */
  theme?: Theme;
}

export const DEFAULT_APP_PREFERENCES: AppPreferences = {
  bulkThresholdUsd: 0.24,
  playsetMinQty: 4,
  theme: "system",
};

interface AppData {
  lots: PurchaseLot[];
  cards: Card[];
  products: Product[];
  sales: Sale[];
  liveListings: LiveListing[];
  listingTemplates: ListingTemplate[];
  shippingSupplies: ShippingSupply[];
  storageBins: StorageBin[];
  locations: Location[];
  containers: Container[];
  deckTemplates: DeckTemplate[];
  bundleTemplates: BundleTemplate[];
  storeImages: StoreImages;
  priceEntries: PriceEntry[];
  bundleSettings: BundleSettings;
  appPreferences: AppPreferences;
}

const STORAGE_KEY = "tcg_web_data_v1";
const SAVE_DEBOUNCE_MS = 200;

const defaultData: AppData = {
  lots: [],
  cards: [],
  products: [],
  sales: [],
  liveListings: [],
  listingTemplates: STARTER_LISTING_TEMPLATES,
  // Empty by default — the supplies init wizard pulls from
  // STARTER_SHIPPING_SUPPLIES_CATALOG and inserts only the items the operator
  // actually carries (with the right mode + costs).
  shippingSupplies: [],
  storageBins: [],
  locations: [],
  containers: [],
  deckTemplates: [],
  bundleTemplates: STARTER_BUNDLE_TEMPLATES,
  storeImages: {},
  priceEntries: [],
  bundleSettings: DEFAULT_BUNDLE_SETTINGS,
  appPreferences: DEFAULT_APP_PREFERENCES,
};

// One-time migration: the seeded "Bundle Lot" template used to expand
// single-card placeholders, producing single-card output for what's almost
// always a multi-card bundle. TNK-24 replaced its body with one that wraps
// the auto-generated bundle description via {{generated}}. If the user still
// has the previous starter body byte-for-byte (i.e. they never customized it),
// silently swap it for the new one. User-edited copies are left untouched.
const OLD_BUNDLE_LOT_BODY =
  "{{cardName}} Bundle - {{setName}}\n\nCondition: {{condition}} (unless otherwise noted)\nAsking: {{price}}\n\nAll cards ship together in protective sleeves inside a rigid top loader or card saver. Bundle deals available — send an offer!\n\nCheck out my store for more lots and singles.";

function migrateListingTemplates(input: ListingTemplate[]): ListingTemplate[] {
  return input.map((t) => {
    if (t.id === "tmpl-starter-2" && t.body === OLD_BUNDLE_LOT_BODY) {
      const fresh = STARTER_LISTING_TEMPLATES.find(
        (s) => s.id === "tmpl-starter-2",
      );
      if (fresh) {
        return { ...t, name: fresh.name, body: fresh.body };
      }
    }
    return t;
  });
}

function loadFromStorage(): AppData {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultData;
    const parsed = JSON.parse(raw) as Partial<AppData>;
    return {
      lots: Array.isArray(parsed.lots) ? parsed.lots : [],
      cards: Array.isArray(parsed.cards) ? parsed.cards : [],
      products: Array.isArray(parsed.products) ? parsed.products : [],
      sales: Array.isArray(parsed.sales) ? parsed.sales : [],
      liveListings: Array.isArray(parsed.liveListings)
        ? parsed.liveListings
        : [],
      listingTemplates:
        Array.isArray(parsed.listingTemplates) &&
        parsed.listingTemplates.length > 0
          ? migrateListingTemplates(parsed.listingTemplates)
          : STARTER_LISTING_TEMPLATES,
      shippingSupplies: Array.isArray(parsed.shippingSupplies)
        ? parsed.shippingSupplies
        : [],
      storageBins: Array.isArray(parsed.storageBins) ? parsed.storageBins : [],
      locations: Array.isArray(parsed.locations) ? parsed.locations : [],
      containers: Array.isArray(parsed.containers) ? parsed.containers : [],
      deckTemplates: Array.isArray(parsed.deckTemplates)
        ? parsed.deckTemplates
        : [],
      bundleTemplates:
        Array.isArray(parsed.bundleTemplates) &&
        parsed.bundleTemplates.length > 0
          ? parsed.bundleTemplates
          : STARTER_BUNDLE_TEMPLATES,
      storeImages:
        parsed.storeImages && typeof parsed.storeImages === "object"
          ? (parsed.storeImages as StoreImages)
          : {},
      priceEntries: Array.isArray(parsed.priceEntries)
        ? parsed.priceEntries
        : [],
      bundleSettings:
        parsed.bundleSettings && typeof parsed.bundleSettings === "object"
          ? { ...DEFAULT_BUNDLE_SETTINGS, ...parsed.bundleSettings }
          : DEFAULT_BUNDLE_SETTINGS,
      appPreferences:
        parsed.appPreferences && typeof parsed.appPreferences === "object"
          ? { ...DEFAULT_APP_PREFERENCES, ...parsed.appPreferences }
          : DEFAULT_APP_PREFERENCES,
    };
  } catch {
    return defaultData;
  }
}

// ─── Context ─────────────────────────────────────────────────────────────────

export interface CardPatch {
  id: string;
  patch: Partial<Card>;
}

interface AppContextValue {
  lots: PurchaseLot[];
  cards: Card[];
  products: Product[];
  sales: Sale[];
  liveListings: LiveListing[];
  listingTemplates: ListingTemplate[];
  shippingSupplies: ShippingSupply[];
  storageBins: StorageBin[];
  locations: Location[];
  containers: Container[];
  deckTemplates: DeckTemplate[];
  bundleTemplates: BundleTemplate[];
  storeImages: StoreImages;
  priceEntries: PriceEntry[];
  appPreferences: AppPreferences;

  // Lot mutations
  addLot: (lot: PurchaseLot) => void;
  updateLot: (lot: PurchaseLot) => void;
  deleteLot: (lotId: string) => void;

  // Card mutations
  addCard: (card: Card) => void;
  addCards: (cards: Card[]) => Promise<void>;
  /** Cache-only upsert of server-authoritative rows — no re-sync. For flows
   *  where the server already persisted the change (e.g. chaos intake). */
  mergeServerCards: (cards: Card[]) => void;
  updateCard: (card: Card) => void;
  deleteCard: (cardId: string) => void;
  bulkUpdateCards: (patches: CardPatch[]) => void;
  splitCardStack: (cardId: string) => void;
  bulkDeleteCards: (cardIds: string[]) => void;
  bulkUpdateCardLocation: (cardIds: string[], storageLocation: string) => void;
  bulkUpdateCardLot: (cardIds: string[], sourceLotId: string) => void;
  clearCardLocations: (cardIds: string[]) => void;
  /** Soft-delete a card (TNK-109). Sets `archivedAt`. Server refuses (409)
   *  if the card is currently packaged into a product — the resolved
   *  error string is returned so the caller can show "unpack first". */
  archiveCard: (cardId: string) => Promise<{ error?: string }>;
  /** Clear archivedAt on a card. */
  restoreCard: (cardId: string) => Promise<{ error?: string }>;

  // Product mutations
  addProduct: (product: Product) => void;
  /**
   * Like addProduct, but also marks every card in product.cardIds as
   * "In Package" with packagedProductId set. Used by the Studio so packaged
   * cards are reserved against double-selling.
   */
  addProductWithPackaging: (product: Product) => void;
  updateProduct: (product: Product) => void;
  deleteProduct: (productId: string) => void;
  /** Unmarks every packaged card (clears packagedProductId, returns listingStatus to "Unlisted") then removes the product. */
  deleteProductAndUnpackCards: (productId: string) => void;
  /** Soft-delete a product (TNK-102). Sets `archivedAt`. Server refuses
   *  (409) if any Active LiveListing is still attached — the resolved
   *  error string is returned so the caller can show "End N listings
   *  first" before retrying. Cards remain reserved in their In-Package
   *  state, so a follow-up Restore brings the bundle back intact. */
  archiveProduct: (productId: string) => Promise<{ error?: string }>;
  /** Clear archivedAt on a product. */
  restoreProduct: (productId: string) => Promise<{ error?: string }>;
  addCardsToProduct: (productId: string, cardIds: string[]) => void;
  removeCardFromProduct: (productId: string, cardId: string) => void;

  // Sale mutations
  addSale: (sale: Sale) => void;
  updateSale: (sale: Sale) => void;
  deleteSale: (saleId: string) => void;
  /** Cancel a sale post-fact (TNK-111). Optimistically flips cancelledAt
   *  + the reverse-side state (cards, supplies, linked listing) when
   *  restock=true; awaits the server's atomic reversal; rolls back on
   *  rejection. Returns an error string when the server refuses (already
   *  cancelled, picked-without-restock, network error). */
  cancelSale: (
    saleId: string,
    body: CancelSaleBody,
  ) => Promise<{ error?: string }>;
  /** Refund a buyer post-fact (TNK-175). Independent of cancel — works on any
   *  not-yet-refunded sale, full or partial. Optimistically flips
   *  refundedAt/refundCents/refundReason, awaits the server, rolls back on
   *  rejection. Returns an error string when the server refuses. */
  refundSale: (
    saleId: string,
    body: RefundSaleBody,
  ) => Promise<{ error?: string }>;

  // Live listing mutations
  addLiveListing: (listing: LiveListing) => void;
  updateLiveListing: (listing: LiveListing) => void;
  deleteLiveListing: (listingId: string) => void;
  /** Seed a server-created product + listing into local state without
   *  firing a create-sync (TNK-112). Used by the listing-import wizard
   *  when the API has already persisted both rows — calling addProduct
   *  here would re-POST and 409 the row. Idempotent: existing matches by
   *  id are left alone. */
  mergeImportedListing: (product: Product, listing: LiveListing) => void;
  /** End a listing (TNK-102). Flips the row's status to 'Ended' locally and
   *  via POST /live-listings/:id/end. When `pushExternal` is true, the
   *  server also attempts an eBay endItem call; failures don't roll back
   *  the local end. Resolves with the external error string (if any) so
   *  the caller can surface a banner. */
  endLiveListing: (
    listingId: string,
    opts?: { reason?: string; pushExternal?: boolean },
  ) => Promise<{ externalError?: string }>;
  goLive: (
    productId: string,
    listing: LiveListing,
    packagingCostDelta?: number,
  ) => void;
  /** Add another listing for an existing product (TNK-102 cross-listing).
   *  The old listing is NOT auto-ended — a Product can have multiple Active
   *  listings across venues (eBay + Facebook + …). Use endLiveListing to
   *  explicitly close a prior one if the operator wants the old behavior. */
  relist: (
    productId: string,
    oldListingId: string,
    newListing: LiveListing,
    packagingCostDelta?: number,
  ) => void;
  updateListingSupplies: (listingId: string, supplyIds: string[]) => void;
  /** Complete a sale (TNK-102). Locally flips the winning listing to
   *  status='Sold' and every other Active listing for the same product to
   *  status='Cancelled' (endReason='superseded'). Server mirrors this
   *  atomically in the same transaction that decrements supplies. Returns
   *  the supersede summary so the caller can render the "ended N other
   *  listings" toast. */
  completeSale: (
    productId: string,
    listingId: string,
    sale: Sale,
    cardCount?: number,
  ) => Promise<{ supersededListings: SupersededListingSummary[] }>;

  // Template mutations
  addTemplate: (template: ListingTemplate) => void;
  updateTemplate: (template: ListingTemplate) => void;
  deleteTemplate: (templateId: string) => void;
  addDeckTemplate: (template: DeckTemplate) => void;
  deleteDeckTemplate: (templateId: string) => void;
  addBundleTemplate: (template: BundleTemplate) => void;
  updateBundleTemplate: (
    templateId: string,
    patch: Partial<BundleTemplate>,
  ) => void;
  deleteBundleTemplate: (templateId: string) => void;

  // Shipping supply mutations
  addShippingSupply: (
    supply: ShippingSupply,
    opts?: { skipSync?: boolean },
  ) => void;
  updateShippingSupply: (supply: ShippingSupply) => void;
  deleteShippingSupply: (supplyId: string) => void;
  restockSupply: (
    supplyId: string,
    batch: { addedQty: number; totalCost: number; supplier?: string },
  ) => Promise<RestockEvent>;

  // Bin mutations
  addBin: (bin: StorageBin) => void;
  updateBin: (bin: StorageBin) => void;
  deleteBin: (binId: string) => void;

  // Location mutations (TNK-103 Phase G)
  addLocation: (loc: Location) => void;
  updateLocation: (loc: Location) => void;
  archiveLocation: (id: string) => Promise<{ error?: string }>;
  restoreLocation: (id: string) => Promise<{ error?: string }>;

  // Container mutations (TNK-187 / epic TNK-186)
  addContainer: (c: Container) => void;
  updateContainer: (c: Container) => void;
  archiveContainer: (id: string) => Promise<{ error?: string }>;
  restoreContainer: (id: string) => Promise<{ error?: string }>;
  deleteContainer: (id: string) => Promise<{ error?: string }>;

  // Price entry mutations (Price Tracker — local-only)
  addPriceEntry: (entry: PriceEntry) => void;
  deletePriceEntry: (entryId: string) => void;

  // Bundle generator settings (local-only preference)
  bundleSettings: BundleSettings;
  updateBundleSettings: (settings: BundleSettings) => void;

  // Listing thresholds + other local-only preferences
  updateAppPreferences: (patch: Partial<AppPreferences>) => Promise<void>;

  // Store branding images (logo, watermark, …)
  setStoreImage: (kind: StoreImageKind, imageRef: string | null) => void;

  // Server-sync metadata (set when a user signs in)
  /** True once the initial server fetch of lots/storageBins resolves.
   *  Note: cards hydrate separately (TNK-155 phase D) — see `cardsLoaded`. */
  inventoryHydrated: boolean;
  /** TNK-155 phase D — cards now hydrate after the main inventory bundle
   *  so sign-in isn't blocked on the ~50MB legacy payload. Reads true
   *  once `loadCards` resolves. Surfaces that need the full set should
   *  check this OR call `loadCards()` to ensure it's loaded. */
  cardsLoaded: boolean;
  /** True while the cards fetch is in flight. UI can show a small
   *  "loading…" hint on surfaces that depend on the full set. */
  cardsLoading: boolean;
  /** Trigger the cards fetch. Idempotent — deduplicates parallel
   *  callers and no-ops if cards are already loaded. */
  loadCards: () => Promise<void>;
  /** Most recent sync error, if any. UI banners read this; `clearSyncError` dismisses. */
  syncError: string | null;
  clearSyncError: () => void;
  /** Offers to upload local-only data to the server. Resolves when the
   *  upload completes (or rejects on first failure). */
  uploadLocalInventory: () => Promise<void>;
  /** True when the server returned empty for all three slices AND
   *  localStorage has data — the migration banner watches this. */
  migrationAvailable: boolean;
}

const AppContext = createContext<AppContextValue | null>(null);

export function AppProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const useServerSync = auth.isAuthenticated && !auth.isGuest;
  // Templates + store settings (TNK-84) are Manager+ surfaces. Gate their
  // hydrate/migration/sync so a Staff session never tries to upload its
  // starter-template defaults (which would 403). Other catalog slices stay
  // open because they're empty locally for Staff anyway.
  const useCatalogConfigSync =
    useServerSync && roleAtLeast(auth.role, "manager");

  const [data, setData] = useState<AppData>(loadFromStorage);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Server-sync state ──────────────────────────────────────────────────
  const [inventoryHydrated, setInventoryHydrated] = useState(!useServerSync);
  const [syncError, setSyncError] = useState<string | null>(null);
  const clearSyncError = useCallback(() => setSyncError(null), []);
  // Snapshot of localStorage at the moment we first contact the server.
  // Used by the migration banner to offer "upload these N records?"
  const preMigrationSnapshot = useRef<{
    lots: PurchaseLot[];
    cards: Card[];
    storageBins: StorageBin[];
  } | null>(null);
  const [migrationAvailable, setMigrationAvailable] = useState(false);

  // Bootstrap: when the user is authenticated, replace the inventory slices
  // with whatever the server has. If the server is empty AND localStorage
  // had data, surface the migration option without overwriting state — the
  // user can choose to upload or to start fresh.
  // TNK-155 phase D — split the cards fetch out of the sign-in
  // hydration bundle so /sales, /tools, /settings, etc. render
  // immediately. Cards still load in the background and stream into
  // the AppContext when ready; the surfaces that need them (analytics,
  // bundle engine, lot stats — see [[tnk-155-inventory-migration]])
  // can either wait via `cardsLoaded` or trigger an explicit
  // `loadCards()` call.
  useEffect(() => {
    if (!useServerSync) {
      setInventoryHydrated(true);
      setMigrationAvailable(false);
      return;
    }
    setInventoryHydrated(false);
    let cancelled = false;
    void (async () => {
      try {
        const [lots, locations, containers] = await Promise.all([
          apiFetchLots(),
          // Locations land alongside the inventory bundle (TNK-103 Phase G).
          // Failure is non-fatal because the seed is best-effort on legacy
          // stores; empty locations just means no Location selector in the UI.
          apiFetchLocations().catch(() => [] as Location[]),
          // Containers (TNK-187/191) — the single source for bins too now.
          apiFetchContainers().catch(() => [] as Container[]),
        ]);
        if (cancelled) return;
        // Migration-snapshot check now only covers lots + storageBins
        // (the cards fetch is deferred). Same semantics for the
        // legacy first-sign-in case.
        const serverEmpty = lots.length === 0 && containers.length === 0;
        setData((prev) => {
          if (
            serverEmpty &&
            (prev.lots.length > 0 || prev.containers.length > 0)
          ) {
            preMigrationSnapshot.current = {
              lots: prev.lots,
              cards: prev.cards,
              storageBins: prev.storageBins,
            };
            setMigrationAvailable(true);
            return prev;
          }
          return { ...prev, lots, locations, containers };
        });
      } catch (err) {
        if (!cancelled) {
          setSyncError(
            err instanceof Error
              ? `Failed to load inventory from server: ${err.message}`
              : "Failed to load inventory from server.",
          );
        }
      } finally {
        if (!cancelled) setInventoryHydrated(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [useServerSync]);

  // TNK-155 phase D — deferred cards hydration. Runs after the main
  // inventory bundle (above) lands so sign-in isn't blocked on the
  // potentially-50MB cards payload. Surfaces that need the full set
  // can also call `loadCards()` explicitly; this background loader
  // covers the warm-start case so most consumers don't notice the
  // change.
  const [cardsLoaded, setCardsLoaded] = useState(false);
  const [cardsLoading, setCardsLoading] = useState(false);
  const loadCardsInflightRef = useRef<Promise<void> | null>(null);

  const loadCards = useCallback(async (): Promise<void> => {
    if (!useServerSync) return;
    if (cardsLoaded) return;
    const inflight = loadCardsInflightRef.current;
    if (inflight) return inflight;
    setCardsLoading(true);
    const p = (async () => {
      try {
        const cards = await apiFetchCards();
        setData((prev) => ({ ...prev, cards }));
        setCardsLoaded(true);
      } catch (err) {
        setSyncError(
          err instanceof Error
            ? `Failed to load cards from server: ${err.message}`
            : "Failed to load cards from server.",
        );
      } finally {
        setCardsLoading(false);
        loadCardsInflightRef.current = null;
      }
    })();
    loadCardsInflightRef.current = p;
    return p;
  }, [useServerSync, cardsLoaded]);

  // Auto-trigger the cards load once the main inventory bundle
  // settles. Surfaces that need cards before the operator notices
  // (analytics dashboard etc.) tend to be the routes the operator
  // navigates to next anyway, so kicking off the fetch in the
  // background here avoids a per-page "loading…" flicker.
  useEffect(() => {
    if (!useServerSync) return;
    if (!inventoryHydrated) return;
    if (cardsLoaded || cardsLoading) return;
    void loadCards();
  }, [useServerSync, inventoryHydrated, cardsLoaded, cardsLoading, loadCards]);

  // Bootstrap the shared catalog (products + sales, TNK-82). Separate from the
  // inventory hydrate because inventory is already server-side, so its
  // "server empty" check never fires for the catalog. On first sign-in with a
  // non-empty local catalog we capture a snapshot + flag a migration; the
  // watcher below uploads it. Otherwise the server is authoritative.
  // localStorage keeps a copy (the persistence effect doesn't clear these
  // slices), so there is no clear-before-upload race.
  //
  // Manager+ only: every catalog route is Manager+, so a Staff session must
  // never fetch or migrate the catalog. Otherwise a browser that holds catalog
  // data in localStorage (e.g. the owner's cached data on a shared machine)
  // would make a Staff session POST to a /bulk route and 403.
  useEffect(() => {
    if (!useCatalogConfigSync) return;
    let cancelled = false;
    void (async () => {
      try {
        const [
          serverProducts,
          serverSales,
          serverLiveListings,
          serverListingTemplates,
          serverDeckTemplates,
          serverBundleTemplates,
          serverShippingSupplies,
          serverPriceEntries,
          serverStoreSettings,
        ] = await Promise.all([
          fetchProducts(),
          fetchSales(),
          fetchLiveListings(),
          fetchListingTemplates(),
          fetchDeckTemplates(),
          fetchBundleTemplates(),
          fetchShippingSupplies(),
          fetchPriceEntries(),
          fetchStoreSettings(),
        ]);
        if (cancelled) return;
        setData((prev) => {
          // Resolve each catalog slice independently. A slice that's empty on
          // the server but populated locally still needs migrating; a slice
          // the server already holds is authoritative. Combining them into a
          // single "server empty" gate is wrong once any slice has shipped:
          // e.g. products/sales migrated in TNK-82, so when live listings
          // (TNK-83) arrived their empty server table would otherwise have
          // overwritten the only local copy. Migrate per-slice instead.
          // TNK-213 — server is authoritative for the shared catalog (the
          // TNK-82 localStorage→DB migration is retired). localStorage is now a
          // pure cache: whatever the server returns, including an empty slice,
          // replaces the local copy. We no longer re-upload a stale cache to an
          // empty server — that silently resurrected deleted catalog data after
          // a wipe (the re-hydration loop). A fetch *error* still throws, so the
          // outer catch keeps prior state and a hiccup can't blank real data.
          const products = serverProducts;
          const sales = serverSales;
          const liveListings = serverLiveListings;

          // Templates, supplies, price entries + store settings (managers only;
          // otherwise the server* values are null and the local slices are
          // left untouched).
          let listingTemplates = prev.listingTemplates;
          let deckTemplates = prev.deckTemplates;
          let bundleTemplates = prev.bundleTemplates;
          let shippingSupplies = prev.shippingSupplies;
          let priceEntries = prev.priceEntries;
          let bundleSettings = prev.bundleSettings;
          let appPreferences = prev.appPreferences;

          // Manager-only slices come back null for staff sessions — leave the
          // local copy untouched then; otherwise the server value (incl. empty)
          // is authoritative.
          if (serverListingTemplates) listingTemplates = serverListingTemplates;
          if (serverDeckTemplates) deckTemplates = serverDeckTemplates;
          if (serverBundleTemplates) bundleTemplates = serverBundleTemplates;
          if (serverShippingSupplies) shippingSupplies = serverShippingSupplies;
          if (serverPriceEntries) priceEntries = serverPriceEntries;
          if (serverStoreSettings) {
            const hasServerSettings =
              serverStoreSettings.bundleSettings !== undefined ||
              serverStoreSettings.appPreferences !== undefined;
            if (hasServerSettings) {
              bundleSettings = {
                ...DEFAULT_BUNDLE_SETTINGS,
                ...(serverStoreSettings.bundleSettings ?? {}),
              };
              appPreferences = {
                ...DEFAULT_APP_PREFERENCES,
                ...(serverStoreSettings.appPreferences ?? {}),
              };
            }
            // No server settings row yet: keep the local defaults; the next
            // settings write (PUT) creates the row — no auto-upload.
          }

          return {
            ...prev,
            products,
            sales,
            liveListings,
            listingTemplates,
            deckTemplates,
            bundleTemplates,
            shippingSupplies,
            priceEntries,
            bundleSettings,
            appPreferences,
          };
        });
      } catch (err) {
        if (!cancelled) {
          setSyncError(
            err instanceof Error
              ? `Failed to load catalog from server: ${err.message}`
              : "Failed to load catalog from server.",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [useCatalogConfigSync]);

  // Persistence:
  //   - Before sign-in / dev guest: full localStorage (everything in one bucket).
  //   - Authd users with a successful server sync skip the three
  //     server-backed slices (we drop them so data doesn't drift between
  //     two sources of truth).
  //   - Authd users while migration is still pending KEEP localStorage
  //     untouched for the inventory slices — wiping them before the
  //     upload completes destroys the only copy of the user's data.
  useEffect(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      try {
        const safeToClearInventory = useServerSync && !migrationAvailable;
        const toSave: AppData = safeToClearInventory
          ? { ...data, lots: [], cards: [], storageBins: [], containers: [] }
          : data;
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(toSave));
      } catch {
        // Quota exceeded or storage disabled — silently drop; next change retries.
      }
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [data, useServerSync, migrationAvailable]);

  const syncOpts = useMemo(
    () => ({ onError: (msg: string) => setSyncError(msg) }),
    [],
  );

  const uploadLocalInventory = useCallback(async () => {
    const snap = preMigrationSnapshot.current;
    if (!snap) return;
    try {
      await uploadAllInventory(snap);
      preMigrationSnapshot.current = null;
      setMigrationAvailable(false);
      setSyncError(null);
    } catch (err) {
      setSyncError(
        err instanceof Error
          ? `Migration failed: ${err.message}`
          : "Migration failed — please retry.",
      );
      throw err;
    }
  }, []);

  // ── Lot mutations ──────────────────────────────────────────────────────────

  const addLot = useCallback(
    (lot: PurchaseLot) => {
      setData((prev) => ({ ...prev, lots: [...prev.lots, lot] }));
      if (useServerSync) syncCreateLot(lot, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const updateLot = useCallback(
    (lot: PurchaseLot) => {
      setData((prev) => ({
        ...prev,
        lots: prev.lots.map((l) => (l.id === lot.id ? lot : l)),
      }));
      if (useServerSync) syncUpdateLot(lot, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const deleteLot = useCallback(
    (lotId: string) => {
      // Collect ids of cards whose sourceLotId is being cleared so we can
      // sync those patches too.
      let clearedCardIds: string[] = [];
      setData((prev) => {
        clearedCardIds = prev.cards
          .filter((c) => c.sourceLotId === lotId)
          .map((c) => c.id);
        return {
          ...prev,
          lots: prev.lots.filter((l) => l.id !== lotId),
          cards: prev.cards.map((c) =>
            c.sourceLotId === lotId ? { ...c, sourceLotId: "" } : c,
          ),
        };
      });
      if (useServerSync) {
        syncDeleteLot(lotId, syncOpts);
        if (clearedCardIds.length > 0) {
          syncBulkPatchCards(
            clearedCardIds.map((id) => ({ id, patch: { sourceLotId: "" } })),
            syncOpts,
          );
        }
      }
    },
    [useServerSync, syncOpts],
  );

  // ── Card mutations ─────────────────────────────────────────────────────────

  const addCard = useCallback(
    (card: Card) => {
      setData((prev) => ({ ...prev, cards: [...prev.cards, card] }));
      if (useServerSync) syncCreateCard(card, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const addCards = useCallback(
    (cards: Card[]): Promise<void> => {
      setData((prev) => ({ ...prev, cards: [...prev.cards, ...cards] }));
      // Local state updates synchronously above (cards visible instantly).
      // The returned promise resolves when the server create lands, letting
      // callers sequence follow-up PATCHes (set/image resolution) after it —
      // avoids the POST-then-PATCH race that silently dropped setCode.
      if (useServerSync) return syncCreateCards(cards, syncOpts);
      return Promise.resolve();
    },
    [useServerSync, syncOpts],
  );

  const updateCard = useCallback(
    (card: Card) => {
      setData((prev) => ({
        ...prev,
        cards: prev.cards.map((c) => (c.id === card.id ? card : c)),
      }));
      if (useServerSync) syncUpdateCard(card, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  // Merge server-authoritative card rows into the cache WITHOUT re-syncing —
  // the server already created/updated them (chaos intake scan/undo). Upserts
  // by id so a freshly-created card appears and an incremented one refreshes.
  const mergeServerCards = useCallback((incoming: Card[]) => {
    if (incoming.length === 0) return;
    setData((prev) => {
      const byId = new Map(prev.cards.map((c) => [c.id, c]));
      for (const c of incoming) byId.set(c.id, c);
      return { ...prev, cards: Array.from(byId.values()) };
    });
  }, []);

  const deleteCard = useCallback(
    (cardId: string) => {
      setData((prev) => ({
        ...prev,
        cards: prev.cards.filter((c) => c.id !== cardId),
      }));
      if (useServerSync) syncDeleteCard(cardId, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const archiveCard = useCallback(
    async (cardId: string): Promise<{ error?: string }> => {
      // Optimistic-first: stamp archivedAt locally so the UI updates before
      // the round-trip. If the server returns 409 ({ error,
      // packagedProductId }) the formatArchiveError helper surfaces the
      // server's "unpack first" message and we roll back the local flag.
      const nowIso = new Date().toISOString();
      setData((prev) => ({
        ...prev,
        cards: prev.cards.map((c) =>
          c.id === cardId ? { ...c, archivedAt: nowIso } : c,
        ),
      }));
      if (!useServerSync) return {};
      try {
        const updated = await archiveCardRequest(cardId);
        setData((prev) => ({
          ...prev,
          cards: prev.cards.map((c) =>
            c.id === cardId ? { ...c, ...updated } : c,
          ),
        }));
        return {};
      } catch (err) {
        setData((prev) => ({
          ...prev,
          cards: prev.cards.map((c) =>
            c.id === cardId ? { ...c, archivedAt: undefined } : c,
          ),
        }));
        return { error: formatArchiveError(err) };
      }
    },
    [useServerSync],
  );

  const restoreCard = useCallback(
    async (cardId: string): Promise<{ error?: string }> => {
      setData((prev) => ({
        ...prev,
        cards: prev.cards.map((c) =>
          c.id === cardId ? { ...c, archivedAt: undefined } : c,
        ),
      }));
      if (!useServerSync) return {};
      try {
        const updated = await restoreCardRequest(cardId);
        setData((prev) => ({
          ...prev,
          cards: prev.cards.map((c) =>
            c.id === cardId ? { ...c, ...updated, archivedAt: undefined } : c,
          ),
        }));
        return {};
      } catch (err) {
        return { error: formatArchiveError(err) };
      }
    },
    [useServerSync],
  );

  const bulkUpdateCards = useCallback(
    (patches: CardPatch[]) => {
      if (patches.length === 0) return;
      const patchMap = new Map(patches.map((p) => [p.id, p.patch]));
      setData((prev) => ({
        ...prev,
        cards: prev.cards.map((c) => {
          const patch = patchMap.get(c.id);
          return patch ? { ...c, ...patch } : c;
        }),
      }));
      if (useServerSync) syncBulkPatchCards(patches, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  // Split a stack of `quantity: N` into N independent `quantity: 1` records,
  // each inheriting the source's fields with a fresh id, so each copy can
  // carry its own photo / condition / location / cost basis / status.
  const splitCardStack = useCallback(
    (cardId: string) => {
      const source = data.cards.find((c) => c.id === cardId);
      if (!source || source.quantity <= 1) return;
      const copies: Card[] = Array.from({ length: source.quantity }, () => ({
        ...source,
        id: generateId("SKU"),
        quantity: 1,
      }));
      setData((prev) => ({
        ...prev,
        cards: [...prev.cards.filter((c) => c.id !== cardId), ...copies],
      }));
      if (useServerSync) {
        syncDeleteCard(cardId, syncOpts);
        void syncCreateCards(copies, syncOpts);
      }
    },
    [data.cards, useServerSync, syncOpts],
  );

  // ── Supertype + rarity backfill (ported from mobile AppContext) ────────
  //
  // Ran once per hydration cycle. Two passes:
  //
  //   1. Sync re-tag — cards with `tcgSupertype: "Trainer"|"Energy"` whose
  //      rarity slipped to Common/Uncommon/Rare get re-tagged to match the
  //      supertype. Cheap, no network.
  //
  //   2. Async lookup — cards missing `tcgSupertype` entirely (legacy rows
  //      from imports before we captured it) are looked up against
  //      pokemontcg.io 4-at-a-time; the response feeds `mapRarity` so the
  //      bucket reflects the real supertype + rarity. Dedup by
  //      setCode|cardNumber so a stack of 6 copies only triggers one fetch.
  //
  // Both passes call `bulkUpdateCards`, which fans patches out to the server
  // for authed users.
  const backfillRan = useRef(false);
  useEffect(() => {
    if (!inventoryHydrated) {
      backfillRan.current = false;
      return;
    }
    if (backfillRan.current) return;
    // TNK-108: skip the catalog enrichment entirely on buyer-facing /shop/*
    // routes. Operators on /products / /inventory still get the supertype
    // backfill; buyers don't need it, and the 404 noise it generates in
    // DevTools is especially noticeable when the operator demos /shop.
    if (
      typeof window !== "undefined" &&
      window.location.pathname.startsWith("/shop")
    ) {
      return;
    }
    // TNK-155 phase F hot-fix: don't fire the catalog enrichment when the
    // operator isn't authenticated. Pre-phase-D this was implicitly safe
    // because `inventoryHydrated` only flipped true after a successful
    // server fetch. Phase D made hydration flip true on the no-auth path
    // too (so guest/signed-out pages render); the enrichment then iterated
    // the localStorage cards cache and each fetchCardMeta call 401'd,
    // which the apiClient handler translated into notifyUnauthorized →
    // logout → re-render — a tight runaway loop hammering
    // /api/card-catalog/* on every signed-out load.
    if (!useServerSync) return;
    backfillRan.current = true;

    const cards = data.cards;
    const syncFixes: CardPatch[] = [];
    for (const c of cards) {
      const sup = c.tcgSupertype?.toLowerCase();
      if (sup === "trainer" && c.rarity !== "Trainer") {
        syncFixes.push({ id: c.id, patch: { rarity: "Trainer" } });
      } else if (sup === "energy" && c.rarity !== "Energy") {
        syncFixes.push({ id: c.id, patch: { rarity: "Energy" } });
      }
    }
    if (syncFixes.length > 0) bulkUpdateCards(syncFixes);

    const cancelRef = { current: false };
    let cancelled = false;
    void (async () => {
      // TNK-120: per-card enrichment is now game-aware. Pokémon stays on
      // `fetchCardMeta` (pokemontcg.io direct, no auth, fastest path).
      // Magic dispatches to `searchCardByNameAndNumber(..., "Magic")` —
      // the same DB-cached Scryfall proxy the CSV importer uses — and
      // splits the returned `typeLine` into supertype + types + subtypes
      // so MTG cards aren't stuck with a stripped Card Details panel
      // forever. Games with no resolver wired are skipped.
      const seen = new Set<string>();
      const toCheck: Card[] = [];
      for (const c of cards) {
        if (!c.setCode || !c.cardNumber) continue;
        if (c.game !== "Pokemon" && c.game !== "Magic") continue;
        // TNK-118: re-fire enrichment whenever a Pokémon row is missing
        // ANY of the storefront-detail fields, not just supertype. A
        // tcgSupertype-only short-circuit left thousands of legacy rows
        // with null tcgTypes / tcgArtist / tcgPokedexNumbers forever,
        // and the storefront detail page silently dropped those chips.
        // TNK-120 follow-up: widen the Magic guard so a row whose
        // tcgTypes was nulled (operator cleanup of pre-TNK-121 junk
        // like ["Magic: The Gathering"]) re-enriches from Scryfall.
        // Without this the Magic backfill would skip on supertype-set
        // and leave the cleaned cards permanently empty.
        const needsEnrichment =
          c.game === "Pokemon"
            ? !c.tcgSupertype ||
              !c.tcgTypes ||
              !c.tcgArtist ||
              !c.tcgPokedexNumbers
            : !c.tcgSupertype || !c.tcgTypes;
        if (!needsEnrichment) continue;
        const key = `${c.game}|${c.setCode.toLowerCase()}-${c.cardNumber}`;
        if (seen.has(key)) continue;
        seen.add(key);
        toCheck.push(c);
      }
      interface Update {
        supertype: string;
        subtypes?: string[];
        types?: string[];
        rarity: CardRarity;
        artist?: string;
        year?: string;
        language?: string;
        // TNK-118: Pokémon-native fields.
        pokedexNumbers?: number[];
        series?: string;
        // TNK-121 MTG-native fields.
        mtgManaCost?: string;
        mtgPowerToughness?: string;
        mtgLoyalty?: string;
        mtgOracleText?: string;
        mtgColors?: string[];
      }
      const updates = new Map<string, Update>();
      const keyFor = (c: Card) =>
        `${c.game}|${c.setCode.toLowerCase()}-${c.cardNumber}`;
      let cursor = 0;
      const worker = async () => {
        while (cursor < toCheck.length && !cancelled) {
          // Bail early if pokemontcg.io has tripped the rate-limit
          // breaker. Every fetchCardMeta call after that would
          // short-circuit to null anyway — no point keeping the
          // loop alive and spinning the worker.
          if (isUpstreamRateLimited()) return;
          const idx = cursor++;
          const card = toCheck[idx];
          if (card.game === "Pokemon") {
            const meta = await fetchCardMeta(card.setCode, card.cardNumber);
            if (cancelled) return;
            if (meta && meta.supertype) {
              updates.set(keyFor(card), {
                supertype: meta.supertype,
                subtypes: meta.subtypes,
                // TNK-118: capture the rest of the storefront-detail
                // fields. Each lands in the patch only when the card is
                // currently missing it (see the patch block below).
                types: meta.types,
                artist: meta.artist,
                pokedexNumbers: meta.nationalPokedexNumbers,
                series: meta.set?.series,
                rarity: mapRarity(
                  meta.rarity ?? card.rarity,
                  meta.supertype,
                  meta.subtypes,
                  meta.hp,
                ),
              });
            }
            continue;
          }
          // Magic — Scryfall via our DB-cached proxy.
          const resolved = await searchCardByNameAndNumber(
            card.name,
            card.cardNumber,
            "Magic",
          );
          if (cancelled) return;
          if (!resolved) continue;
          const split = splitMtgTypeLine(resolved.typeLine);
          if (!split.supertype && split.types.length === 0) continue;
          const yearMatch = resolved.releasedAt
            ? /^(\d{4})/.exec(resolved.releasedAt)
            : null;
          updates.set(keyFor(card), {
            // Supertype column is single-text; types[] feeds the chip row.
            // When MTG has no supertype ("Instant"), fall back to the
            // first type so the existing "skip cards with supertype" guard
            // continues to short-circuit on re-mount.
            supertype: split.supertype ?? split.types[0] ?? "",
            subtypes: split.subtypes.length > 0 ? split.subtypes : undefined,
            types: split.types.length > 0 ? split.types : undefined,
            rarity: mapRarity(
              resolved.rarity ?? card.rarity,
              undefined,
              undefined,
              undefined,
            ),
            artist: resolved.artist,
            year: yearMatch ? yearMatch[1] : undefined,
            language: resolved.language,
            mtgManaCost: resolved.mtgManaCost,
            mtgPowerToughness: resolved.mtgPowerToughness,
            mtgLoyalty: resolved.mtgLoyalty,
            mtgOracleText: resolved.mtgOracleText,
            mtgColors: resolved.mtgColors,
          });
        }
      };
      // Two workers in parallel — enough throughput when the upstream
      // is healthy, gentle enough to avoid instant 429 storms. The
      // session breaker (above) kills both on the first rate-limit.
      await Promise.all([worker(), worker()]);
      if (cancelled) return;

      // Pass 1+2 patches: enrichment that fired for cards with a setCode
      // (the per-card resolver path). Skip when no updates were collected
      // — but still fall through to Pass 3, which handles the orthogonal
      // empty-setCode case (was bypassed pre-TNK-120 when no Pokemon
      // cards needed enrichment).
      if (updates.size > 0) {
        const patches: CardPatch[] = [];
        for (const c of cards) {
          if (!c.setCode || !c.cardNumber) continue;
          if (c.game !== "Pokemon" && c.game !== "Magic") continue;
          // TNK-118: mirror the toCheck gate so we still patch Pokémon
          // rows whose supertype is already filled but tcgTypes /
          // tcgArtist / tcgPokedexNumbers are not.
          const needsEnrichment =
            c.game === "Pokemon"
              ? !c.tcgSupertype ||
                !c.tcgTypes ||
                !c.tcgArtist ||
                !c.tcgPokedexNumbers
              : !c.tcgSupertype;
          if (!needsEnrichment) continue;
          const u = updates.get(keyFor(c));
          if (!u || !u.supertype) continue;
          patches.push({
            id: c.id,
            patch: {
              ...(c.tcgSupertype ? {} : { tcgSupertype: u.supertype }),
              ...(c.tcgSubtypes || !u.subtypes
                ? {}
                : { tcgSubtypes: u.subtypes }),
              ...(u.types && !c.tcgTypes ? { tcgTypes: u.types } : {}),
              ...(c.tcgSupertype ? {} : { rarity: u.rarity }),
              ...(u.artist && !c.tcgArtist ? { tcgArtist: u.artist } : {}),
              ...(u.pokedexNumbers && !c.tcgPokedexNumbers
                ? { tcgPokedexNumbers: u.pokedexNumbers }
                : {}),
              ...(u.series && !c.tcgSeries ? { tcgSeries: u.series } : {}),
              ...(u.year && !c.year ? { year: u.year } : {}),
              ...(u.language && !c.language ? { language: u.language } : {}),
              // TNK-121 MTG-native fields.
              ...(u.mtgManaCost && !c.mtgManaCost
                ? { mtgManaCost: u.mtgManaCost }
                : {}),
              ...(u.mtgPowerToughness && !c.mtgPowerToughness
                ? { mtgPowerToughness: u.mtgPowerToughness }
                : {}),
              ...(u.mtgLoyalty && !c.mtgLoyalty
                ? { mtgLoyalty: u.mtgLoyalty }
                : {}),
              ...(u.mtgOracleText && !c.mtgOracleText
                ? { mtgOracleText: u.mtgOracleText }
                : {}),
              ...(u.mtgColors && !c.mtgColors
                ? { mtgColors: u.mtgColors }
                : {}),
            },
          });
        }
        if (patches.length > 0) bulkUpdateCards(patches);
      }

      // Pass 3: re-resolve set/rarity/finish for empty-setCode cards. Shared
      // worker also called by the CSV importers so freshly-imported cards
      // don't have to wait for the next reload — see features/cardSetBackfill.ts.
      await backfillCardSets(cards, { bulkUpdateCards, cancelled: cancelRef });
    })();
    return () => {
      cancelled = true;
      cancelRef.current = true;
    };
    // Intentionally depends only on `inventoryHydrated`: we want this to run
    // exactly once per hydration. Adding `data.cards` would refire on every
    // card mutation and cancel the in-flight async lookup; the freshly-
    // hydrated cards are captured in closure at the moment hydration flips.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryHydrated]);

  const bulkDeleteCards = useCallback(
    (cardIds: string[]) => {
      if (cardIds.length === 0) return;
      const ids = new Set(cardIds);
      setData((prev) => ({
        ...prev,
        cards: prev.cards.filter((c) => !ids.has(c.id)),
      }));
      if (useServerSync) syncBulkDeleteCards(cardIds, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const bulkUpdateCardLocation = useCallback(
    (cardIds: string[], storageLocation: string) => {
      if (cardIds.length === 0) return;
      const ids = new Set(cardIds);
      setData((prev) => ({
        ...prev,
        cards: prev.cards.map((c) =>
          ids.has(c.id) ? { ...c, storageLocation } : c,
        ),
      }));
      if (useServerSync) {
        syncBulkPatchCards(
          cardIds.map((id) => ({ id, patch: { storageLocation } })),
          syncOpts,
        );
      }
    },
    [useServerSync, syncOpts],
  );

  const bulkUpdateCardLot = useCallback(
    (cardIds: string[], sourceLotId: string) => {
      if (cardIds.length === 0) return;
      const ids = new Set(cardIds);
      setData((prev) => ({
        ...prev,
        cards: prev.cards.map((c) =>
          ids.has(c.id) ? { ...c, sourceLotId } : c,
        ),
      }));
      if (useServerSync) {
        syncBulkPatchCards(
          cardIds.map((id) => ({ id, patch: { sourceLotId } })),
          syncOpts,
        );
      }
    },
    [useServerSync, syncOpts],
  );

  // ── Bin mutations ──────────────────────────────────────────────────────────

  // Bins are containers now (TNK-191). These wrappers keep the legacy API (the
  // bin picker's inline-create + the flat page) working while writing the
  // single `containers` source. The derived `storageBins` view reflects them.
  // parentId uses the site-container id scheme so the derived locationId
  // round-trips.
  const addBin = useCallback(
    (bin: StorageBin) => {
      const c: Container = {
        id: bin.id,
        kind: "bin",
        name: bin.name,
        color: bin.color,
        parentId: bin.locationId ? `CTN-LOC-${bin.locationId}` : undefined,
        state: "open",
        createdAt: bin.createdAt,
      };
      setData((prev) => ({ ...prev, containers: [...prev.containers, c] }));
      if (useServerSync) syncCreateContainer(c, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const updateBin = useCallback(
    (bin: StorageBin) => {
      const renamedCardIds: string[] = [];
      const newName = bin.name;
      let nextContainer: Container | undefined;
      setData((prev) => {
        const old = prev.containers.find((c) => c.id === bin.id);
        const oldName = old?.name ?? "";
        const shouldRename = oldName !== "" && oldName !== newName;
        const cards = shouldRename
          ? prev.cards.map((c) => {
              if (c.storageLocation === oldName) {
                renamedCardIds.push(c.id);
                return { ...c, storageLocation: newName };
              }
              return c;
            })
          : prev.cards;
        nextContainer = {
          ...(old ?? {
            id: bin.id,
            kind: "bin",
            state: "open",
            createdAt: bin.createdAt,
          }),
          id: bin.id,
          kind: "bin",
          name: bin.name,
          color: bin.color,
          parentId: bin.locationId
            ? `CTN-LOC-${bin.locationId}`
            : old?.parentId,
        } as Container;
        return {
          ...prev,
          containers: prev.containers.map((c) =>
            c.id === bin.id ? nextContainer! : c,
          ),
          cards,
        };
      });
      if (useServerSync) {
        if (nextContainer) syncUpdateContainer(nextContainer, syncOpts);
        if (renamedCardIds.length > 0) {
          syncBulkPatchCards(
            renamedCardIds.map((id) => ({
              id,
              patch: { storageLocation: newName },
            })),
            syncOpts,
          );
        }
      }
    },
    [useServerSync, syncOpts],
  );

  const deleteBin = useCallback(
    (binId: string) => {
      const clearedCardIds: string[] = [];
      setData((prev) => {
        const bin = prev.containers.find((c) => c.id === binId);
        const binName = bin?.name ?? "";
        const cards = binName
          ? prev.cards.map((c) => {
              if (c.storageLocation === binName) {
                clearedCardIds.push(c.id);
                return { ...c, storageLocation: "" };
              }
              return c;
            })
          : prev.cards;
        return {
          ...prev,
          containers: prev.containers.filter((c) => c.id !== binId),
          cards,
        };
      });
      if (useServerSync) {
        void deleteContainerRequest(binId).catch(() => {});
        if (clearedCardIds.length > 0) {
          syncBulkPatchCards(
            clearedCardIds.map((id) => ({
              id,
              patch: { storageLocation: "" },
            })),
            syncOpts,
          );
        }
      }
    },
    [useServerSync, syncOpts],
  );

  // ── Locations (TNK-103 Phase G) ──────────────────────────────────────────
  const addLocation = useCallback(
    (loc: Location) => {
      setData((prev) => ({ ...prev, locations: [...prev.locations, loc] }));
      if (useServerSync) syncCreateLocation(loc, syncOpts);
    },
    [useServerSync, syncOpts],
  );
  const updateLocation = useCallback(
    (loc: Location) => {
      setData((prev) => ({
        ...prev,
        locations: prev.locations.map((l) => (l.id === loc.id ? loc : l)),
      }));
      if (useServerSync) syncUpdateLocation(loc, syncOpts);
    },
    [useServerSync, syncOpts],
  );
  // Archive / restore follow the optimistic-flip-with-rollback pattern from
  // archiveProduct (TNK-103 Phase E). No 409 shape from the server today,
  // but the structure is in place for if/when it grows one.
  const archiveLocation = useCallback(
    async (id: string): Promise<{ error?: string }> => {
      const nowIso = new Date().toISOString();
      setData((prev) => ({
        ...prev,
        locations: prev.locations.map((l) =>
          l.id === id ? { ...l, archivedAt: nowIso } : l,
        ),
      }));
      if (!useServerSync) return {};
      try {
        const updated = await archiveLocationRequest(id);
        setData((prev) => ({
          ...prev,
          locations: prev.locations.map((l) =>
            l.id === id ? { ...l, ...updated } : l,
          ),
        }));
        return {};
      } catch (err) {
        setData((prev) => ({
          ...prev,
          locations: prev.locations.map((l) =>
            l.id === id ? { ...l, archivedAt: undefined } : l,
          ),
        }));
        return {
          error: err instanceof Error ? err.message : "Failed to archive",
        };
      }
    },
    [useServerSync],
  );
  const restoreLocation = useCallback(
    async (id: string): Promise<{ error?: string }> => {
      setData((prev) => ({
        ...prev,
        locations: prev.locations.map((l) =>
          l.id === id ? { ...l, archivedAt: undefined } : l,
        ),
      }));
      if (!useServerSync) return {};
      try {
        const updated = await restoreLocationRequest(id);
        setData((prev) => ({
          ...prev,
          locations: prev.locations.map((l) =>
            l.id === id ? { ...l, ...updated, archivedAt: undefined } : l,
          ),
        }));
        return {};
      } catch (err) {
        return {
          error: err instanceof Error ? err.message : "Failed to restore",
        };
      }
    },
    [useServerSync],
  );

  // ── Containers (TNK-187 / epic TNK-186) ──────────────────────────────────
  // Additive: the storage tree alongside bins/locations. Optimistic add/update;
  // archive/restore mirror the location pattern; delete awaits the server (it
  // 409s on a non-empty node) and only removes locally on success.
  const addContainer = useCallback(
    (c: Container) => {
      setData((prev) => ({ ...prev, containers: [...prev.containers, c] }));
      if (useServerSync) syncCreateContainer(c, syncOpts);
    },
    [useServerSync, syncOpts],
  );
  const updateContainer = useCallback(
    (c: Container) => {
      const renamedCardIds: string[] = [];
      setData((prev) => {
        const old = prev.containers.find((x) => x.id === c.id);
        // A bin-kind container is referenced by cards via its NAME (TNK-191),
        // so a rename must propagate to cards.storage_location.
        const renaming =
          old?.kind === "bin" &&
          c.kind === "bin" &&
          old.name !== "" &&
          old.name !== c.name;
        const cards = renaming
          ? prev.cards.map((card) => {
              if (card.storageLocation === old!.name) {
                renamedCardIds.push(card.id);
                return { ...card, storageLocation: c.name };
              }
              return card;
            })
          : prev.cards;
        return {
          ...prev,
          containers: prev.containers.map((x) => (x.id === c.id ? c : x)),
          cards,
        };
      });
      if (useServerSync) {
        syncUpdateContainer(c, syncOpts);
        if (renamedCardIds.length > 0) {
          syncBulkPatchCards(
            renamedCardIds.map((id) => ({
              id,
              patch: { storageLocation: c.name },
            })),
            syncOpts,
          );
        }
      }
    },
    [useServerSync, syncOpts],
  );
  const archiveContainer = useCallback(
    async (id: string): Promise<{ error?: string }> => {
      const nowIso = new Date().toISOString();
      setData((prev) => ({
        ...prev,
        containers: prev.containers.map((c) =>
          c.id === id ? { ...c, archivedAt: nowIso } : c,
        ),
      }));
      if (!useServerSync) return {};
      try {
        const updated = await archiveContainerRequest(id);
        setData((prev) => ({
          ...prev,
          containers: prev.containers.map((c) =>
            c.id === id ? { ...c, ...updated } : c,
          ),
        }));
        return {};
      } catch (err) {
        setData((prev) => ({
          ...prev,
          containers: prev.containers.map((c) =>
            c.id === id ? { ...c, archivedAt: undefined } : c,
          ),
        }));
        return {
          error: err instanceof Error ? err.message : "Failed to archive",
        };
      }
    },
    [useServerSync],
  );
  const restoreContainer = useCallback(
    async (id: string): Promise<{ error?: string }> => {
      setData((prev) => ({
        ...prev,
        containers: prev.containers.map((c) =>
          c.id === id ? { ...c, archivedAt: undefined } : c,
        ),
      }));
      if (!useServerSync) return {};
      try {
        const updated = await restoreContainerRequest(id);
        setData((prev) => ({
          ...prev,
          containers: prev.containers.map((c) =>
            c.id === id ? { ...c, ...updated, archivedAt: undefined } : c,
          ),
        }));
        return {};
      } catch (err) {
        return {
          error: err instanceof Error ? err.message : "Failed to restore",
        };
      }
    },
    [useServerSync],
  );
  const deleteContainer = useCallback(
    async (id: string): Promise<{ error?: string }> => {
      if (useServerSync) {
        try {
          await deleteContainerRequest(id);
        } catch (err) {
          return {
            error: err instanceof Error ? err.message : "Failed to delete",
          };
        }
      }
      // Deleting a bin-kind container clears its cards' storage_location by name
      // (TNK-191), mirroring the old deleteBin behavior.
      const clearedCardIds: string[] = [];
      setData((prev) => {
        const c = prev.containers.find((x) => x.id === id);
        const binName = c?.kind === "bin" ? c.name : "";
        const cards = binName
          ? prev.cards.map((card) => {
              if (card.storageLocation === binName) {
                clearedCardIds.push(card.id);
                return { ...card, storageLocation: "" };
              }
              return card;
            })
          : prev.cards;
        return {
          ...prev,
          containers: prev.containers.filter((x) => x.id !== id),
          cards,
        };
      });
      if (useServerSync && clearedCardIds.length > 0) {
        syncBulkPatchCards(
          clearedCardIds.map((cid) => ({
            id: cid,
            patch: { storageLocation: "" },
          })),
          syncOpts,
        );
      }
      return {};
    },
    [useServerSync, syncOpts],
  );

  // storageBins is now a DERIVED view of bin-kind containers (TNK-191) — the
  // single source for bins. The existing consumers (bin picker, inventory +
  // product filters, bin colors, sales pick) read this unchanged. locationId
  // round-trips via the CTN-LOC- parent-id scheme used by the backfill + addBin.
  const derivedStorageBins = useMemo<StorageBin[]>(
    () =>
      data.containers
        .filter((c) => c.kind === "bin" && !c.archivedAt)
        .map((c) => ({
          id: c.id,
          name: c.name,
          color: c.color ?? "#6b7280",
          locationId: c.parentId?.startsWith("CTN-LOC-")
            ? c.parentId.slice("CTN-LOC-".length)
            : undefined,
          createdAt: c.createdAt,
        })),
    [data.containers],
  );

  // ── Store branding images ────────────────────────────────────────────────
  const setStoreImage = useCallback(
    (kind: StoreImageKind, imageRef: string | null) => {
      setData((prev) => {
        const next: StoreImages = { ...prev.storeImages };
        if (imageRef) next[kind] = imageRef;
        else delete next[kind];
        return { ...prev, storeImages: next };
      });
    },
    [],
  );

  const clearCardLocations = useCallback(
    (cardIds: string[]) => {
      if (cardIds.length === 0) return;
      const ids = new Set(cardIds);
      setData((prev) => ({
        ...prev,
        cards: prev.cards.map((c) =>
          ids.has(c.id) ? { ...c, storageLocation: "" } : c,
        ),
      }));
      if (useServerSync) {
        syncBulkPatchCards(
          cardIds.map((id) => ({ id, patch: { storageLocation: "" } })),
          syncOpts,
        );
      }
    },
    [useServerSync, syncOpts],
  );

  // ── Product mutations ─────────────────────────────────────────────────────

  const addProduct = useCallback(
    (product: Product) => {
      setData((prev) => ({ ...prev, products: [...prev.products, product] }));
      if (useServerSync) syncCreateProduct(product, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const addProductWithPackaging = useCallback(
    (product: Product) => {
      setData((prev) => {
        const packagedIds = new Set(product.cardIds);
        return {
          ...prev,
          products: [...prev.products, product],
          cards: prev.cards.map((c) =>
            packagedIds.has(c.id)
              ? {
                  ...c,
                  listingStatus: "In Package",
                  packagedProductId: product.id,
                }
              : c,
          ),
        };
      });
      if (useServerSync) {
        syncCreateProduct(product, syncOpts);
        if (product.cardIds.length > 0) {
          syncBulkPatchCards(
            product.cardIds.map((id) => ({
              id,
              patch: {
                listingStatus: "In Package",
                packagedProductId: product.id,
              },
            })),
            syncOpts,
          );
        }
      }
    },
    [useServerSync, syncOpts],
  );

  const updateProduct = useCallback(
    (product: Product) => {
      setData((prev) => ({
        ...prev,
        products: prev.products.map((p) => (p.id === product.id ? product : p)),
      }));
      if (useServerSync) syncUpdateProduct(product, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const deleteProduct = useCallback(
    (productId: string) => {
      setData((prev) => ({
        ...prev,
        products: prev.products.filter((p) => p.id !== productId),
      }));
      if (useServerSync) syncDeleteProduct(productId, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  // callApi packs the server JSON body into Error.message after a "→ STATUS — "
  // separator. For archive/restore the body is `{ error, activeListingIds? }`;
  // we lift that into a one-sentence banner the operator can act on.
  function formatArchiveError(err: unknown): string {
    if (!(err instanceof Error)) return "Failed to archive";
    const raw = err.message;
    const m = raw.match(/→\s*\d+\s*—\s*(\{.*\})/);
    if (!m) return raw;
    try {
      const body = JSON.parse(m[1]) as {
        error?: string;
        activeListingIds?: string[];
      };
      const ids = body.activeListingIds ?? [];
      if (ids.length > 0) {
        const n = ids.length;
        return `Can't archive — this product still has ${n} active listing${n === 1 ? "" : "s"}. End ${n === 1 ? "it" : "them"} on the Live page first.`;
      }
      return body.error ?? raw;
    } catch {
      return raw;
    }
  }

  const archiveProduct = useCallback(
    async (productId: string): Promise<{ error?: string }> => {
      // Optimistic-first: flip archivedAt locally so the UI updates before
      // the round-trip. If the server returns 409 (Active listings still
      // attached), roll back and surface the error so the caller can show
      // "End N listings first" or similar.
      const nowIso = new Date().toISOString();
      setData((prev) => ({
        ...prev,
        products: prev.products.map((p) =>
          p.id === productId ? { ...p, archivedAt: nowIso } : p,
        ),
      }));
      if (!useServerSync) return {};
      try {
        const updated = await archiveProductRequest(productId);
        setData((prev) => ({
          ...prev,
          products: prev.products.map((p) =>
            p.id === productId ? { ...p, ...updated } : p,
          ),
        }));
        return {};
      } catch (err) {
        // Roll back the optimistic update.
        setData((prev) => ({
          ...prev,
          products: prev.products.map((p) =>
            p.id === productId ? { ...p, archivedAt: undefined } : p,
          ),
        }));
        return { error: formatArchiveError(err) };
      }
    },
    [useServerSync],
  );

  const restoreProduct = useCallback(
    async (productId: string): Promise<{ error?: string }> => {
      // The local mirror is the inverse — clear archivedAt up front; roll
      // back if the server rejects.
      setData((prev) => ({
        ...prev,
        products: prev.products.map((p) =>
          p.id === productId ? { ...p, archivedAt: undefined } : p,
        ),
      }));
      if (!useServerSync) return {};
      try {
        const updated = await restoreProductRequest(productId);
        setData((prev) => ({
          ...prev,
          products: prev.products.map((p) =>
            p.id === productId
              ? { ...p, ...updated, archivedAt: undefined }
              : p,
          ),
        }));
        return {};
      } catch (err) {
        return { error: formatArchiveError(err) };
      }
    },
    [useServerSync],
  );

  const deleteProductAndUnpackCards = useCallback(
    (productId: string) => {
      let unpackedCardIds: string[] = [];
      let removedListingIds: string[] = [];
      setData((prev) => {
        unpackedCardIds = prev.cards
          .filter((c) => c.packagedProductId === productId)
          .map((c) => c.id);
        removedListingIds = prev.liveListings
          .filter((l) => l.productId === productId)
          .map((l) => l.id);
        return {
          ...prev,
          products: prev.products.filter((p) => p.id !== productId),
          cards: prev.cards.map((c) =>
            c.packagedProductId === productId
              ? {
                  ...c,
                  packagedProductId: undefined,
                  listingStatus: "Unlisted",
                }
              : c,
          ),
          liveListings: prev.liveListings.filter(
            (l) => l.productId !== productId,
          ),
        };
      });
      if (useServerSync) {
        syncDeleteProduct(productId, syncOpts);
        for (const id of removedListingIds) syncDeleteLiveListing(id, syncOpts);
        if (unpackedCardIds.length > 0) {
          syncBulkPatchCards(
            unpackedCardIds.map((id) => ({
              id,
              patch: { listingStatus: "Unlisted", packagedProductId: null },
            })),
            syncOpts,
          );
        }
      }
    },
    [useServerSync, syncOpts],
  );

  const addCardsToProduct = useCallback(
    (productId: string, cardIds: string[]) => {
      if (cardIds.length === 0) return;
      const adding = new Set(cardIds);
      let updatedProduct: Product | undefined;
      setData((prev) => {
        const product = prev.products.find((p) => p.id === productId);
        if (!product) return prev;
        const mergedIds = Array.from(new Set([...product.cardIds, ...cardIds]));
        updatedProduct = {
          ...product,
          cardIds: mergedIds,
          cardCount: mergedIds.length,
        };
        return {
          ...prev,
          products: prev.products.map((p) =>
            p.id === productId ? updatedProduct! : p,
          ),
          cards: prev.cards.map((c) =>
            adding.has(c.id)
              ? {
                  ...c,
                  listingStatus: "In Package",
                  packagedProductId: productId,
                }
              : c,
          ),
        };
      });
      if (useServerSync) {
        if (updatedProduct) syncUpdateProduct(updatedProduct, syncOpts);
        syncBulkPatchCards(
          cardIds.map((id) => ({
            id,
            patch: {
              listingStatus: "In Package",
              packagedProductId: productId,
            },
          })),
          syncOpts,
        );
      }
    },
    [useServerSync, syncOpts],
  );

  const removeCardFromProduct = useCallback(
    (productId: string, cardId: string) => {
      let updatedProduct: Product | undefined;
      setData((prev) => {
        const product = prev.products.find((p) => p.id === productId);
        if (!product) return prev;
        const newIds = product.cardIds.filter((id) => id !== cardId);
        updatedProduct = {
          ...product,
          cardIds: newIds,
          cardCount: newIds.length,
        };
        return {
          ...prev,
          products: prev.products.map((p) =>
            p.id === productId ? updatedProduct! : p,
          ),
          cards: prev.cards.map((c) =>
            c.id === cardId
              ? {
                  ...c,
                  packagedProductId: undefined,
                  listingStatus: "Unlisted",
                }
              : c,
          ),
        };
      });
      if (useServerSync) {
        if (updatedProduct) syncUpdateProduct(updatedProduct, syncOpts);
        syncBulkPatchCards(
          [
            {
              id: cardId,
              patch: { listingStatus: "Unlisted", packagedProductId: null },
            },
          ],
          syncOpts,
        );
      }
    },
    [useServerSync, syncOpts],
  );

  // ── Sale mutations ────────────────────────────────────────────────────────

  const addSale = useCallback(
    (sale: Sale) => {
      setData((prev) => ({ ...prev, sales: [...prev.sales, sale] }));
      if (useServerSync) syncCreateSale(sale, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const updateSale = useCallback(
    (sale: Sale) => {
      setData((prev) => ({
        ...prev,
        sales: prev.sales.map((s) => (s.id === sale.id ? sale : s)),
      }));
      if (useServerSync) syncUpdateSale(sale, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const deleteSale = useCallback(
    (saleId: string) => {
      setData((prev) => ({
        ...prev,
        sales: prev.sales.filter((s) => s.id !== saleId),
      }));
      if (useServerSync) syncDeleteSale(saleId, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const cancelSale = useCallback(
    async (
      saleId: string,
      body: CancelSaleBody,
    ): Promise<{ error?: string }> => {
      // Capture the pre-cancel sale + the reverse-side state we mutate so
      // we can roll back atomically on server rejection. The server is the
      // authority for the reversal — we mirror locally for UI snappiness,
      // then trust the response (or undo) once it lands.
      const nowIso = new Date().toISOString();
      let priorSale: Sale | undefined;
      let priorCards: Card[] = [];
      let priorListing: LiveListing | undefined;
      let priorSupplies: ShippingSupply[] = [];

      setData((prev) => {
        const sale = prev.sales.find((s) => s.id === saleId);
        if (!sale) return prev;
        priorSale = sale;

        // Decide which cards to release (mirrors server logic).
        let cardIdsToRelease: string[] = [];
        if (body.restock) {
          if (sale.itemType === "Card" && sale.itemId) {
            cardIdsToRelease = [sale.itemId];
          } else if (sale.itemType === "Product" && sale.itemId) {
            const product = prev.products.find((p) => p.id === sale.itemId);
            cardIdsToRelease = product?.cardIds ?? [];
          }
        }
        priorCards = prev.cards.filter((c) => cardIdsToRelease.includes(c.id));

        // Snapshot the linked listing so we can roll back if needed.
        if (body.restock && sale.listingId) {
          priorListing = prev.liveListings.find((l) => l.id === sale.listingId);
        }

        // Snapshot the supplies we'll re-credit so rollback is exact.
        const breakdown = sale.suppliesBreakdown ?? [];
        priorSupplies = prev.shippingSupplies.filter((sup) =>
          breakdown.some((b) => b.id === sup.id && (b.consumedQty ?? 0) > 0),
        );

        const nextSales = prev.sales.map((s) =>
          s.id === saleId
            ? { ...s, cancelledAt: nowIso, cancelReason: body.reason }
            : s,
        );

        if (!body.restock) {
          return { ...prev, sales: nextSales };
        }

        const nextCards = prev.cards.map((c) =>
          cardIdsToRelease.includes(c.id) &&
          (sale.itemType !== "Product" || c.packagedProductId === sale.itemId)
            ? {
                ...c,
                packagedProductId: undefined,
                listingStatus: "Unlisted" as const,
              }
            : c,
        );

        const nextListings = sale.listingId
          ? prev.liveListings.map((l) =>
              l.id === sale.listingId && l.status === "Sold"
                ? {
                    ...l,
                    status: "Active" as ListingStatus,
                    endedAt: undefined,
                    endReason: undefined,
                  }
                : l,
            )
          : prev.liveListings;

        const nextSupplies = prev.shippingSupplies.map((sup) => {
          const credit = breakdown.find((b) => b.id === sup.id);
          if (!credit || !credit.consumedQty) return sup;
          return { ...sup, quantity: (sup.quantity ?? 0) + credit.consumedQty };
        });

        return {
          ...prev,
          sales: nextSales,
          cards: nextCards,
          liveListings: nextListings,
          shippingSupplies: nextSupplies,
        };
      });

      if (!useServerSync) return {};

      try {
        const updated = await cancelSaleRequest(saleId, body);
        setData((prev) => ({
          ...prev,
          sales: prev.sales.map((s) =>
            s.id === saleId ? { ...s, ...updated } : s,
          ),
        }));
        return {};
      } catch (err) {
        // Roll back every piece we touched. The captured snapshots are the
        // exact pre-cancel state so the rollback is byte-for-byte correct.
        setData((prev) => ({
          ...prev,
          sales: priorSale
            ? prev.sales.map((s) => (s.id === saleId ? priorSale! : s))
            : prev.sales,
          cards: prev.cards.map((c) => {
            const restore = priorCards.find((p) => p.id === c.id);
            return restore ?? c;
          }),
          liveListings: priorListing
            ? prev.liveListings.map((l) =>
                l.id === priorListing!.id ? priorListing! : l,
              )
            : prev.liveListings,
          shippingSupplies: prev.shippingSupplies.map((sup) => {
            const restore = priorSupplies.find((p) => p.id === sup.id);
            return restore ?? sup;
          }),
        }));
        return { error: formatArchiveError(err) };
      }
    },
    [useServerSync],
  );

  const refundSale = useCallback(
    async (
      saleId: string,
      body: RefundSaleBody,
    ): Promise<{ error?: string }> => {
      // Refund only touches the sale row (no cards/supplies/listing), so the
      // rollback snapshot is just the prior sale. Optimistic flip, await the
      // authoritative server row, undo on rejection.
      const nowIso = new Date().toISOString();
      let priorSale: Sale | undefined;

      setData((prev) => {
        const sale = prev.sales.find((s) => s.id === saleId);
        if (!sale) return prev;
        priorSale = sale;
        return {
          ...prev,
          sales: prev.sales.map((s) =>
            s.id === saleId
              ? {
                  ...s,
                  refundedAt: nowIso,
                  refundCents: body.amountCents,
                  refundReason: body.reason,
                }
              : s,
          ),
        };
      });

      if (!useServerSync) return {};

      try {
        const updated = await refundSaleRequest(saleId, body);
        setData((prev) => ({
          ...prev,
          sales: prev.sales.map((s) =>
            s.id === saleId ? { ...s, ...updated } : s,
          ),
        }));
        return {};
      } catch (err) {
        setData((prev) => ({
          ...prev,
          sales: priorSale
            ? prev.sales.map((s) => (s.id === saleId ? priorSale! : s))
            : prev.sales,
        }));
        return { error: formatArchiveError(err) };
      }
    },
    [useServerSync],
  );

  // ── Live listing mutations ────────────────────────────────────────────────

  const mergeImportedListing = useCallback(
    (product: Product, listing: LiveListing) => {
      // Local-only seed for listing-import (TNK-112). The server has
      // already persisted both rows; addProduct/addLiveListing would
      // re-POST and 409 against the existing rows.
      setData((prev) => ({
        ...prev,
        products: prev.products.some((p) => p.id === product.id)
          ? prev.products
          : [...prev.products, product],
        liveListings: prev.liveListings.some((l) => l.id === listing.id)
          ? prev.liveListings
          : [...prev.liveListings, listing],
      }));
    },
    [],
  );

  const addLiveListing = useCallback(
    (listing: LiveListing) => {
      setData((prev) => ({
        ...prev,
        liveListings: [...prev.liveListings, listing],
      }));
      if (useServerSync) syncCreateLiveListing(listing, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const updateLiveListing = useCallback(
    (listing: LiveListing) => {
      setData((prev) => ({
        ...prev,
        liveListings: prev.liveListings.map((l) =>
          l.id === listing.id ? listing : l,
        ),
      }));
      if (useServerSync) syncUpdateLiveListing(listing, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const deleteLiveListing = useCallback(
    (listingId: string) => {
      setData((prev) => ({
        ...prev,
        liveListings: prev.liveListings.filter((l) => l.id !== listingId),
      }));
      if (useServerSync) syncDeleteLiveListing(listingId, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const endLiveListing = useCallback(
    async (
      listingId: string,
      opts?: { reason?: string; pushExternal?: boolean },
    ): Promise<{ externalError?: string }> => {
      const reason = opts?.reason ?? "manual";
      const pushExternal = opts?.pushExternal === true;

      // Local-first: flip status to Ended so the UI updates immediately. If the
      // product has no OTHER Active listings, also flip product status back to
      // "Ready to List" so the operator can re-list.
      let endedAtIso = new Date().toISOString();
      let updatedProduct: Product | undefined;
      setData((prev) => {
        const listing = prev.liveListings.find((l) => l.id === listingId);
        if (!listing) return prev;
        const otherActive = prev.liveListings.some(
          (l) =>
            l.productId === listing.productId &&
            l.id !== listingId &&
            (l.status ?? "Active") === "Active",
        );
        const products = prev.products.map((p) => {
          if (p.id !== listing.productId) return p;
          if (otherActive) return p;
          updatedProduct = { ...p, status: "Ready to List" };
          return updatedProduct;
        });
        return {
          ...prev,
          liveListings: prev.liveListings.map((l) =>
            l.id === listingId
              ? {
                  ...l,
                  status: "Ended" as ListingStatus,
                  endedAt: endedAtIso,
                  endReason: reason,
                }
              : l,
          ),
          products,
        };
      });

      if (!useServerSync) return {};
      try {
        const res = await endLiveListingRequest(listingId, {
          reason,
          pushExternal,
        });
        // Mirror externalEndedOk + canonical timestamps from the server.
        setData((prev) => ({
          ...prev,
          liveListings: prev.liveListings.map((l) =>
            l.id === listingId ? { ...l, ...res.listing } : l,
          ),
        }));
        if (updatedProduct) syncUpdateProduct(updatedProduct, syncOpts);
        return { externalError: res.externalError };
      } catch (err) {
        // Server rejected the end (e.g. 409 not-active) — log and surface,
        // but keep the optimistic local Ended state so the UI is consistent.
        return {
          externalError: err instanceof Error ? err.message : String(err),
        };
      }
    },
    [useServerSync, syncOpts],
  );

  const goLive = useCallback(
    (productId: string, listing: LiveListing, packagingCostDelta = 0) => {
      let updatedProduct: Product | undefined;
      setData((prev) => {
        const products = prev.products.map((p) => {
          if (p.id !== productId) return p;
          updatedProduct = {
            ...p,
            status: "Listed",
            packagingCost: p.packagingCost + packagingCostDelta,
          };
          return updatedProduct;
        });
        return {
          ...prev,
          liveListings: [...prev.liveListings, listing],
          products,
        };
      });
      if (useServerSync) {
        syncCreateLiveListing(listing, syncOpts);
        if (updatedProduct) syncUpdateProduct(updatedProduct, syncOpts);
      }
    },
    [useServerSync, syncOpts],
  );

  const relist = useCallback(
    (
      productId: string,
      _oldListingId: string,
      newListing: LiveListing,
      packagingCostDelta = 0,
    ) => {
      // Cross-listing (TNK-102): the old listing is NOT removed — both
      // continue to live until one closes. If the operator wants the old
      // listing ended, they call endLiveListing explicitly. We keep the
      // oldListingId parameter only for source-compat with existing
      // callers.
      let updatedProduct: Product | undefined;
      setData((prev) => {
        const products = prev.products.map((p) => {
          if (p.id !== productId) return p;
          updatedProduct = {
            ...p,
            status: "Listed",
            packagingCost: p.packagingCost + packagingCostDelta,
          };
          return updatedProduct;
        });
        return {
          ...prev,
          liveListings: [...prev.liveListings, newListing],
          products,
        };
      });
      if (useServerSync) {
        syncCreateLiveListing(newListing, syncOpts);
        if (updatedProduct) syncUpdateProduct(updatedProduct, syncOpts);
      }
    },
    [useServerSync, syncOpts],
  );

  const updateListingSupplies = useCallback(
    (listingId: string, supplyIds: string[]) => {
      let updatedListing: LiveListing | undefined;
      setData((prev) => ({
        ...prev,
        liveListings: prev.liveListings.map((l) => {
          if (l.id !== listingId) return l;
          updatedListing = { ...l, supplyIds };
          return updatedListing;
        }),
      }));
      if (useServerSync && updatedListing)
        syncUpdateLiveListing(updatedListing, syncOpts);
    },
    [useServerSync, syncOpts],
  );

  const completeSale = useCallback(
    async (
      productId: string,
      listingId: string,
      sale: Sale,
      cardCount?: number,
    ): Promise<{ supersededListings: SupersededListingSummary[] }> => {
      // Local-first supersede mirror (TNK-102). Flip the winning listing to
      // 'Sold' and every OTHER Active listing for the same product to
      // 'Cancelled'/'superseded'. Server does the same atomically inside
      // the POST /sales transaction; this just lets the UI update before
      // the round-trip lands.
      const nowIso = new Date().toISOString();
      const isManualSale = !listingId;
      let updatedProduct: Product | undefined;
      let supersededLocal: SupersededListingSummary[] = [];
      const saleWithListing: Sale =
        sale.listingId || isManualSale ? sale : { ...sale, listingId };

      setData((prev) => {
        const products = prev.products.map((p) => {
          if (p.id !== productId) return p;
          updatedProduct = { ...p, status: "Sold" };
          return updatedProduct;
        });
        // Manual sale path — no listingId means there's no winning listing
        // and nothing to supersede; just add the sale and flip product status.
        if (isManualSale) {
          return { ...prev, sales: [...prev.sales, saleWithListing], products };
        }
        supersededLocal = prev.liveListings
          .filter(
            (l) =>
              l.productId === productId &&
              l.id !== listingId &&
              (l.status ?? "Active") === "Active",
          )
          .map((l) => ({
            id: l.id,
            venue: l.venue,
            ebayListingId: l.ebayListingId,
          }));
        const liveListings = prev.liveListings.map((l) => {
          if (l.id === listingId) {
            return {
              ...l,
              status: "Sold" as ListingStatus,
              endedAt: nowIso,
              endReason: "sold",
            };
          }
          if (
            l.productId === productId &&
            (l.status ?? "Active") === "Active"
          ) {
            return {
              ...l,
              status: "Cancelled" as ListingStatus,
              endedAt: nowIso,
              endReason: "superseded",
            };
          }
          return l;
        });
        return {
          ...prev,
          sales: [...prev.sales, saleWithListing],
          liveListings,
          products,
        };
      });

      if (!useServerSync) {
        return { supersededListings: supersededLocal };
      }

      try {
        const res = await createSale(saleWithListing, { cardCount });
        if (updatedProduct) syncUpdateProduct(updatedProduct, syncOpts);
        // Trust the server's canonical supersede list over the local mirror.
        const serverSuper = (res.supersededListings ?? []).map((s) => ({
          id: s.id,
          venue: s.venue as SaleVenue,
          ebayListingId: s.ebayListingId,
        }));
        return { supersededListings: serverSuper };
      } catch (err) {
        // POST failed — the local optimistic update already landed; the
        // sync banner will surface the error via fireAndLog (createSale
        // throws raw, so we rethrow for the caller to observe).
        throw err;
      }
    },
    [useServerSync, syncOpts],
  );

  // ── Template mutations ────────────────────────────────────────────────────

  const addTemplate = useCallback(
    (template: ListingTemplate) => {
      setData((prev) => ({
        ...prev,
        listingTemplates: [...prev.listingTemplates, template],
      }));
      if (useCatalogConfigSync) syncCreateListingTemplate(template, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const updateTemplate = useCallback(
    (template: ListingTemplate) => {
      setData((prev) => ({
        ...prev,
        listingTemplates: prev.listingTemplates.map((t) =>
          t.id === template.id ? template : t,
        ),
      }));
      if (useCatalogConfigSync) syncUpdateListingTemplate(template, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const deleteTemplate = useCallback(
    (templateId: string) => {
      setData((prev) => ({
        ...prev,
        listingTemplates: prev.listingTemplates.filter(
          (t) => t.id !== templateId,
        ),
      }));
      if (useCatalogConfigSync) syncDeleteListingTemplate(templateId, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const addDeckTemplate = useCallback(
    (template: DeckTemplate) => {
      setData((prev) => ({
        ...prev,
        deckTemplates: [...prev.deckTemplates, template],
      }));
      if (useCatalogConfigSync) syncCreateDeckTemplate(template, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const deleteDeckTemplate = useCallback(
    (templateId: string) => {
      setData((prev) => ({
        ...prev,
        deckTemplates: prev.deckTemplates.filter((t) => t.id !== templateId),
      }));
      if (useCatalogConfigSync) syncDeleteDeckTemplate(templateId, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const addBundleTemplate = useCallback(
    (template: BundleTemplate) => {
      setData((prev) => ({
        ...prev,
        bundleTemplates: [...prev.bundleTemplates, template],
      }));
      if (useCatalogConfigSync) syncCreateBundleTemplate(template, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const updateBundleTemplate = useCallback(
    (templateId: string, patch: Partial<BundleTemplate>) => {
      let updated: BundleTemplate | undefined;
      setData((prev) => ({
        ...prev,
        bundleTemplates: prev.bundleTemplates.map((t) => {
          if (t.id !== templateId) return t;
          updated = { ...t, ...patch };
          return updated;
        }),
      }));
      if (useCatalogConfigSync && updated)
        syncUpdateBundleTemplate(updated, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const deleteBundleTemplate = useCallback(
    (templateId: string) => {
      setData((prev) => ({
        ...prev,
        bundleTemplates: prev.bundleTemplates.filter(
          (t) => t.id !== templateId,
        ),
      }));
      if (useCatalogConfigSync) syncDeleteBundleTemplate(templateId, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  // ── Shipping supply mutations ─────────────────────────────────────────────

  const addShippingSupply = useCallback(
    (supply: ShippingSupply, opts?: { skipSync?: boolean }) => {
      setData((prev) => ({
        ...prev,
        shippingSupplies: [...prev.shippingSupplies, supply],
      }));
      if (useCatalogConfigSync && !opts?.skipSync)
        syncCreateShippingSupply(supply, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const updateShippingSupply = useCallback(
    (supply: ShippingSupply) => {
      setData((prev) => ({
        ...prev,
        shippingSupplies: prev.shippingSupplies.map((s) =>
          s.id === supply.id ? supply : s,
        ),
      }));
      if (useCatalogConfigSync) syncUpdateShippingSupply(supply, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const deleteShippingSupply = useCallback(
    (supplyId: string) => {
      setData((prev) => ({
        ...prev,
        shippingSupplies: prev.shippingSupplies.filter(
          (s) => s.id !== supplyId,
        ),
      }));
      if (useCatalogConfigSync) syncDeleteShippingSupply(supplyId, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  // Restock a supply (TNK-100). Authoritative on the server — we wait for the
  // returned supply (with refreshed weighted-avg unitCost + full history) and
  // replace local state with it.
  const restockSupply = useCallback(
    async (
      supplyId: string,
      batch: { addedQty: number; totalCost: number; supplier?: string },
    ): Promise<RestockEvent> => {
      const { supply, event } = await restockShippingSupply(supplyId, batch);
      setData((prev) => ({
        ...prev,
        shippingSupplies: prev.shippingSupplies.map((s) =>
          s.id === supply.id ? supply : s,
        ),
      }));
      return event;
    },
    [],
  );

  // ── Price entry mutations ─────────────────────────────────────────────────

  const addPriceEntry = useCallback(
    (entry: PriceEntry) => {
      setData((prev) => ({
        ...prev,
        priceEntries: [...prev.priceEntries, entry],
      }));
      if (useCatalogConfigSync) syncCreatePriceEntry(entry, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const deletePriceEntry = useCallback(
    (entryId: string) => {
      setData((prev) => ({
        ...prev,
        priceEntries: prev.priceEntries.filter((e) => e.id !== entryId),
      }));
      if (useCatalogConfigSync) syncDeletePriceEntry(entryId, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const updateBundleSettings = useCallback(
    (settings: BundleSettings) => {
      let next: StoreSettings | undefined;
      setData((prev) => {
        next = {
          bundleSettings: settings,
          appPreferences: prev.appPreferences,
        };
        return { ...prev, bundleSettings: settings };
      });
      if (useCatalogConfigSync && next) syncStoreSettings(next, syncOpts);
    },
    [useCatalogConfigSync, syncOpts],
  );

  const updateAppPreferences = useCallback(
    async (patch: Partial<AppPreferences>): Promise<void> => {
      // Compute the next settings directly from the closure state. Relying
      // on the setData reducer's side-effects to populate an outer variable
      // is fragile across StrictMode dev double-invocation + concurrent
      // rendering — see the TNK-100 wizard bug that left appPreferences.
      // supplies unsaved on the server.
      const nextPrefs: AppPreferences = { ...data.appPreferences, ...patch };
      const nextSettings: StoreSettings = {
        bundleSettings: data.bundleSettings,
        appPreferences: nextPrefs,
      };
      setData((prev) => ({
        ...prev,
        appPreferences: { ...prev.appPreferences, ...patch },
      }));
      if (useCatalogConfigSync) {
        try {
          await saveStoreSettings(nextSettings);
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          syncOpts.onError?.(`Couldn't save preferences — ${msg}`);
          throw err;
        }
      }
    },
    [data, useCatalogConfigSync, syncOpts],
  );

  return (
    <AppContext.Provider
      value={{
        lots: data.lots,
        cards: data.cards,
        products: data.products,
        sales: data.sales,
        liveListings: data.liveListings,
        listingTemplates: data.listingTemplates,
        shippingSupplies: data.shippingSupplies,
        storageBins: derivedStorageBins,
        locations: data.locations,
        containers: data.containers,
        deckTemplates: data.deckTemplates,
        bundleTemplates: data.bundleTemplates,
        storeImages: data.storeImages,
        priceEntries: data.priceEntries,
        addLot,
        updateLot,
        deleteLot,
        addCard,
        addCards,
        mergeServerCards,
        updateCard,
        deleteCard,
        bulkUpdateCards,
        splitCardStack,
        bulkDeleteCards,
        bulkUpdateCardLocation,
        bulkUpdateCardLot,
        clearCardLocations,
        archiveCard,
        restoreCard,
        addProduct,
        addProductWithPackaging,
        updateProduct,
        deleteProduct,
        deleteProductAndUnpackCards,
        archiveProduct,
        restoreProduct,
        addCardsToProduct,
        removeCardFromProduct,
        addSale,
        updateSale,
        deleteSale,
        cancelSale,
        refundSale,
        mergeImportedListing,
        addLiveListing,
        updateLiveListing,
        deleteLiveListing,
        endLiveListing,
        goLive,
        relist,
        updateListingSupplies,
        completeSale,
        addTemplate,
        updateTemplate,
        deleteTemplate,
        addDeckTemplate,
        deleteDeckTemplate,
        addBundleTemplate,
        updateBundleTemplate,
        deleteBundleTemplate,
        addShippingSupply,
        updateShippingSupply,
        deleteShippingSupply,
        restockSupply,
        addBin,
        updateBin,
        deleteBin,
        addLocation,
        updateLocation,
        archiveLocation,
        restoreLocation,
        addContainer,
        updateContainer,
        archiveContainer,
        restoreContainer,
        deleteContainer,
        addPriceEntry,
        deletePriceEntry,
        bundleSettings: data.bundleSettings,
        updateBundleSettings,
        appPreferences: data.appPreferences,
        updateAppPreferences,
        setStoreImage,
        inventoryHydrated,
        cardsLoaded,
        cardsLoading,
        loadCards,
        syncError,
        clearSyncError,
        uploadLocalInventory,
        migrationAvailable,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp(): AppContextValue {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
